// ---------------------------------------------------------------------------
// L'ECRAN D'ADMINISTRATION DES ACCES — DEUX SECTIONS, PLUS TROIS
// ---------------------------------------------------------------------------
// Toji (13/09), devant la version precedente : « dans les personnes invitees,
// je suis toujours dedans. Nouveau role, il y est toujours. Dans les membres,
// ca n'a pas change. On n'est pas bon la, parce que, dans tous les cas, LES
// PERSONNES INVITEES, EN FAIT, C'EST LES MEMBRES. Donc, en fait, la, en termes
// d'interface, c'est pas du tout ce que je t'ai demande. »
//
// ⚠️ LE DEFAUT ETAIT STRUCTUREL, ET AUCUN REGLAGE D'ALIGNEMENT NE LE REPARAIT.
// L'ecran portait TROIS zones : « Les personnes invitees » (`/api/admin/invites`,
// les sept provenances recoupees), « Membres » (`/api/admin/membres`, le seul
// mapping maison — un SOUS-ENSEMBLE STRICT de la premiere) et les roles. Les
// deux premieres parlaient donc DES MEMES GENS par deux chemins differents :
// poser un role dans l'une laissait l'autre en retard, et la meme personne
// apparaissait deux fois, a deux endroits, avec deux verites.
//
// Il reste DEUX sections :
//   1. LES PERSONNES — une seule liste, celle de `/api/admin/invites`. Ce que
//      la zone « Membres » savait faire et que la liste n'avait pas (editer
//      l'etiquette et la note, ajouter quelqu'un par identifiant Discord) est
//      pose EN LIGNE, dans cette meme liste. Le proprietaire y est a part, en
//      tete : il n'est pas un invite, et le faire disparaitre serait l'autre
//      facon de se tromper.
//   2. LES ROLES ET LEURS PERMISSIONS — la matrice en deux volets (les huit
//      categories a gauche, leurs permissions a droite), la creation d'un role,
//      et la liaison role Discord -> role maison : c'est la meme matiere.
//
// POURQUOI UN FICHIER A PART. `src/main.js` passe les seize mille lignes : y
// ajouter un ecran entier le rend illisible pour tout le monde. Ce module ne
// connait RIEN du lecteur : il recoit ses outils (`$`, `api`, `vue`…) a
// l'assemblage, ce qui le rend aussi eprouvable sans navigateur.
//
// ⚠️ LES DROITS NE SE RECALCULENT PAS ICI. `GET /api/admin/invites` rend
// `droits` ET `origines`, resolus par `droitsDe()` dans access.js — la seule
// fonction qui fasse cette somme. Un ecran qui la referait (proprietaire +
// roles Discord + roles maison, « admin » portant les trois autres) finirait
// par repondre autre chose que `exigeDroit` cote serveur, et « l'ecran dit oui,
// le serveur dit non » est exactement le genre de desaccord qu'on ne
// diagnostique pas. On AFFICHE ce que le serveur a resolu, y compris l'origine
// de chaque droit.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// LA MATRICE, REDITE COTE ECRAN — ET LE CONTRAT QUI LA TIENT
// ---------------------------------------------------------------------------
// ⚠️ CE MODULE N'IMPORTE RIEN, ET CE N'EST PAS NEGOCIABLE. Il vit dans le
// paquet du navigateur ; `access.js` lit le disque (`node:fs`), engendre des
// identifiants (`node:crypto`) et ne peut pas y entrer. La table des
// permissions est donc ECRITE DEUX FOIS — exactement comme `PROVENANCES` /
// `PROVENANCES_LISIBLES` plus bas, et pour la meme raison.
//
// ⚠️ UNE TABLE REDITE EST CE QUI CASSE LE PLUS SILENCIEUSEMENT DU PROJET. Une
// cle renommee d'un seul cote coche une permission que le serveur jette ; un
// `niveau` qui diverge affiche un pourcentage faux sans qu'aucune requete
// echoue. C'est pourquoi `essais/ecran-matrice.mjs` compare les deux tables
// CLE PAR CLE — cle, categorie, niveau, sensible, nom — et fait echouer le banc
// a la premiere divergence. Ne jamais toucher a l'une sans l'autre.
//
// ⚠️ ET `niveauDePouvoir` EST REDITE AVEC ELLE, AU CARACTERE PRES. C'est la
// meme regle : l'ecran ne peut pas appeler celle d'`access.js`. Le banc les
// fait tourner TOUTES LES DEUX sur les memes jeux — les sept roles livres, le
// vide, le complet, chaque permission seule — et exige la meme valeur pour
// `pourcent`, `poids`, `comptees`, `sensibles` et `parNiveau`. Ce que l'ecran
// affiche vient de CE retour-la, jamais d'un second calcul fait dans un gabarit
// (« comptees » recompte a partir des cases cochees, par exemple) : deux
// calculs divergent, et c'est toujours celui qui s'affiche qui aura tort.
// ---------------------------------------------------------------------------

/** Les categories, dans l'ordre ou l'ecran doit les presenter. */
export const CATEGORIES = Object.freeze([
  { cle: 'lecture', icone: '🎬', nom: 'Regarder — le lecteur' },
  { cle: 'bibliotheque', icone: '📚', nom: 'Ma bibliothèque' },
  { cle: 'liens', icone: '🔗', nom: 'Les liens' },
  { cle: 'apercus', icone: '🖼️', nom: 'Les aperçus' },
  { cle: 'visuels', icone: '🎨', nom: 'La bibliothèque du service' },
  { cle: 'sources', icone: '🔌', nom: 'Les add-ons' },
  { cle: 'personnes', icone: '👥', nom: 'Les personnes' },
  { cle: 'service', icone: '⚙️', nom: 'Le service' },
].map(Object.freeze));

/**
 * Les quatre niveaux et leur poids. La progression est RAIDE (1, 3, 8, 20) :
 * il faut vingt « voir le catalogue » pour egaler un `service.admin`, et c'est
 * ce rapport-la qu'on veut lire d'un coup d'oeil.
 */
export const NIVEAUX = Object.freeze([
  { niveau: 1, nom: 'Ordinaire', poids: 1, description: 'Ce que fait un spectateur : voir, regarder, gérer ses propres sources.' },
  { niveau: 2, nom: 'Atelier', poids: 3, description: 'Agir sur le contenu du service : liens, aperçus, visuels, add-ons.' },
  { niveau: 3, nom: 'Gouvernance', poids: 8, description: 'Agir sur les personnes et sur le service lui-même.' },
  { niveau: 4, nom: 'Sensible', poids: 20, description: 'Ouvre la porte par laquelle on distribue toutes les autres.' },
].map(Object.freeze));

/** Toutes les permissions du service, dans l'ordre de la conception. */
export const PERMISSIONS = Object.freeze([
  { cle: 'catalogue.voir', categorie: 'lecture', nom: 'Voir le catalogue', description: 'Chercher, parcourir les collections, ouvrir une fiche et ses rangs — sans pouvoir lire quoi que ce soit.', niveau: 1, sensible: false },
  { cle: 'lecture.regarder', categorie: 'lecture', nom: 'Regarder une œuvre', description: 'Ouvrir un épisode : flux, pistes audio, sous-titres, langue, durée, aperçus, sauts.', niveau: 1, sensible: false },
  { cle: 'lecture.lancer', categorie: 'lecture', nom: 'Lancer une lecture en séance', description: 'Prendre la main sur une séance et imposer la lecture aux autres participants.', niveau: 2, sensible: false },
  { cle: 'lecture.convertir', categorie: 'lecture', nom: 'Demander un remux / transcodage', description: 'Réclamer une conversion (remux, flux brut) — elle consomme le processeur du serveur.', niveau: 2, sensible: false },
  { cle: 'lecture.sans_vocal', categorie: 'lecture', nom: 'Regarder sans être en vocal', description: 'Lancer l’activité et ouvrir le lecteur hors d’un salon vocal : la séance tourne seule, sans pouvoir sur aucun salon. S’accorde par rôle, ou à une personne jusqu’à une échéance.', niveau: 2, sensible: false },

  { cle: 'mes_sources.gerer', categorie: 'bibliotheque', nom: 'Gérer ses propres sources', description: 'Brancher, régler et retirer ses add-ons personnels.', niveau: 1, sensible: false },
  { cle: 'mes_sources.preter', categorie: 'bibliotheque', nom: 'Prêter ses sources', description: 'Ouvrir ses propres sources à quelqu’un d’autre, et affranchir un prêt.', niveau: 2, sensible: false },

  { cle: 'liens.voir', categorie: 'liens', nom: 'Voir les liens d’un épisode', description: 'Consulter le mapping d’un épisode et les sources qui le servent.', niveau: 2, sensible: false },
  { cle: 'liens.mapper', categorie: 'liens', nom: 'Choisir / épingler un lien', description: 'Épingler ou retirer le lien retenu pour un épisode.', niveau: 2, sensible: false },
  { cle: 'liens.sonder', categorie: 'liens', nom: 'Lancer scans et sondes', description: 'Déclencher un scan de catalogue ou une sonde sur un lien.', niveau: 2, sensible: false },
  { cle: 'liens.veiller', categorie: 'liens', nom: 'Suivre la veille des liens', description: 'Consulter et régler la veille qui surveille les liens dans le temps.', niveau: 2, sensible: false },
  { cle: 'liens.adresse', categorie: 'liens', nom: 'Obtenir l’adresse réelle d’un fichier', description: 'Rendre l’URL débridée d’un fichier, utilisable telle quelle hors du lecteur et sans trace.', niveau: 4, sensible: true },

  { cle: 'apercus.fabriquer', categorie: 'apercus', nom: 'Fabriquer une planche', description: 'Demander la fabrication d’une planche d’aperçus pour un épisode ou un lien.', niveau: 2, sensible: false },
  { cle: 'apercus.parc', categorie: 'apercus', nom: 'Voir et purger le parc', description: 'Consulter le parc de planches, en supprimer une, ou le vider entièrement.', niveau: 2, sensible: false },
  { cle: 'apercus.deposer', categorie: 'apercus', nom: 'Déposer une planche fabriquée ailleurs', description: 'Téléverser une planche produite hors du serveur — le travailleur du PC.', niveau: 2, sensible: false },

  { cle: 'visuels.arcs', categorie: 'visuels', nom: 'Modifier les arcs', description: 'Régler les arcs narratifs d’une œuvre et leurs bornes.', niveau: 2, sensible: false },
  { cle: 'visuels.photos', categorie: 'visuels', nom: 'Modifier les visuels', description: 'Changer les affiches, fonds et vignettes du service.', niveau: 2, sensible: false },
  { cle: 'catalogue.editer', categorie: 'visuels', nom: 'Corriger une fiche, ajouter une œuvre', description: 'Modifier les métadonnées d’une œuvre, la description d’une collection, ou ajouter une œuvre au catalogue.', niveau: 2, sensible: false },
  { cle: 'catalogue.mix', categorie: 'visuels', nom: 'Recomposer le mix d’accueil', description: 'Recomposer et amorcer la sélection présentée sur l’accueil.', niveau: 2, sensible: false },

  { cle: 'sources.voir', categorie: 'sources', nom: 'Voir les add-ons du service', description: 'Consulter la liste des add-ons de l’instance et leur état.', niveau: 2, sensible: false },
  { cle: 'sources.gerer', categorie: 'sources', nom: 'Brancher / débrancher un add-on', description: 'Ajouter, régler, actualiser, sonder ou retirer un add-on du service.', niveau: 2, sensible: false },
  { cle: 'sources.adresse', categorie: 'sources', nom: 'Relire l’adresse d’un manifeste', description: 'Rendre l’URL complète d’un add-on, configuration encodée comprise — interrogeable hors du lecteur.', niveau: 4, sensible: true },

  { cle: 'personnes.voir', categorie: 'personnes', nom: 'Voir qui a accès', description: 'Consulter les membres, les invités et le profil de chacun.', niveau: 3, sensible: false },
  { cle: 'personnes.mapper', categorie: 'personnes', nom: 'Poser un rôle sur quelqu’un', description: 'Attribuer ou retirer des rôles maison à une personne.', niveau: 3, sensible: false },
  { cle: 'personnes.inviter', categorie: 'personnes', nom: 'Accepter une demande, inviter', description: 'Traiter les demandes d’accès et nommer des invités.', niveau: 3, sensible: false },
  { cle: 'roles.voir', categorie: 'personnes', nom: 'Voir la matrice', description: 'Consulter les rôles maison et les permissions qu’ils portent.', niveau: 3, sensible: false },
  { cle: 'roles.gerer', categorie: 'personnes', nom: 'Créer et régler les rôles', description: 'Créer, modifier et supprimer un rôle maison, et lier un rôle Discord à un rôle maison.', niveau: 3, sensible: false },
  { cle: 'sessions.voir', categorie: 'personnes', nom: 'Voir qui est connecté', description: 'Consulter la liste des sessions ouvertes.', niveau: 3, sensible: false },
  { cle: 'sessions.fermer', categorie: 'personnes', nom: 'Déconnecter quelqu’un', description: 'Fermer la session d’une personne nommée.', niveau: 3, sensible: false },
  { cle: 'sessions.reset', categorie: 'personnes', nom: 'Déconnecter tout le monde', description: 'Fermer toutes les sessions du service d’un seul geste.', niveau: 4, sensible: true },

  { cle: 'service.notifications', categorie: 'service', nom: 'Régler les notifications Discord', description: 'Salon d’annonce, rôles mentionnés par langue, message de choix.', niveau: 2, sensible: false },
  { cle: 'service.miroir', categorie: 'service', nom: 'Piloter et purger le miroir', description: 'Lancer, régler et vider le miroir des liens.', niveau: 2, sensible: false },
  { cle: 'service.diagnostic', categorie: 'service', nom: 'Lire les diagnostics', description: 'Consulter les conversions, les profils, les sorties réseau et les sauts.', niveau: 2, sensible: false },
  { cle: 'service.reglages', categorie: 'service', nom: 'Serveurs autorisés, salons, goûts, apparence', description: 'Régler l’accès du service, ses salons, ses goûts et son apparence.', niveau: 3, sensible: false },
  { cle: 'service.admin', categorie: 'service', nom: 'Tout, sans exception', description: 'La clé de voûte : ouvre toutes les permissions, y compris celles qui n’existent pas encore.', niveau: 4, sensible: true },
].map(Object.freeze));

/**
 * Les quatre permissions qui ne s'accordent qu'a la main, par un proprietaire.
 *
 * ⚠️ LA LISTE SE DERIVE DE `sensible: true`, ELLE NE SE RECOPIE PAS. Elle a
 * deja ete ecrite a la main une fois, cote serveur, a TROIS entrees au lieu de
 * quatre — et rien n'echouait, puisque toutes les attentes se deduisaient de la
 * meme table amputee.
 */
export const SENSIBLES = Object.freeze(PERMISSIONS.filter((p) => p.sensible).map((p) => p.cle));

/** Le poids d'un niveau. Un niveau inconnu ne vaut RIEN plutot que de valoir tout. */
export const poidsDuNiveau = (niveau) => NIVEAUX.find((n) => n.niveau === niveau)?.poids ?? 0;

/** Le poids de TOUT — le denominateur du pourcentage. Calcule une fois. */
export const POIDS_TOTAL = PERMISSIONS.reduce((t, p) => t + poidsDuNiveau(p.niveau), 0);

/** Une permission par sa cle, ou `null`. */
export const laPermission = (cle) => PERMISSIONS.find((p) => p.cle === String(cle ?? '')) ?? null;

/** Est-ce l'une des quatre ? */
export const estSensible = (cle) => SENSIBLES.includes(String(cle ?? ''));

/** Le nom lisible d'un niveau — « Ordinaire », « Atelier », « Gouvernance », « Sensible ». */
export const nomDuNiveau = (niveau) => NIVEAUX.find((n) => n.niveau === niveau)?.nom ?? 'inconnu';

/** Les permissions d'une categorie, dans l'ordre de la table. PURE. */
export const permissionsDeLaCategorie = (cle) => PERMISSIONS.filter((p) => p.categorie === String(cle ?? ''));

/**
 * Le jeu de permissions d'un role, ramene a un TABLEAU DE CLES connues, dans
 * l'ordre de la table.
 *
 * Accepte les trois formes que le service manipule — un tableau de cles, un
 * objet `{cle: true}`, un role entier (`{permissions}`) — parce que les
 * distinguer chez l'appelant ferait qu'un appelant sur trois passerait la
 * mauvaise. PURE.
 */
export function jeuDuRole(jeu) {
  const brut = Array.isArray(jeu)
    ? jeu.map(String)
    : Array.isArray(jeu?.permissions)
      ? jeu.permissions.map(String)
      : Object.entries(jeu && typeof jeu === 'object' ? (jeu.permissions ?? jeu) : {})
        .filter(([, v]) => v === true).map(([k]) => String(k));
  const voulues = new Set(brut);
  return PERMISSIONS.map((p) => p.cle).filter((cle) => voulues.has(cle));
}

/**
 * Le « niveau de pouvoir » d'un jeu de permissions, en pourcentage pondere.
 *
 * ⚠️ COPIE CONFORME DE `niveauDePouvoir` (access.js) — voir l'encadre en tete
 * de fichier. Le banc fait tourner les deux sur les memes jeux et exige la meme
 * valeur pour chacun des cinq champs.
 */
export function niveauDePouvoir(jeu) {
  const connues = jeuDuRole(jeu);
  const parNiveau = Object.fromEntries(NIVEAUX.map((n) => [n.niveau, 0]));
  let poids = 0;
  for (const c of connues) {
    const p = laPermission(c);
    poids += poidsDuNiveau(p.niveau);
    parNiveau[p.niveau] += 1;
  }
  return {
    // ⚠️ UNE DECIMALE SOUS 10 %, UN ENTIER AU-DESSUS — et c'est mesure. Les
    // quatre sensibles pesent 80 des 204 points : a l'entier, Shinigami
    // (1 point) et Invite (2 points) affichaient TOUS LES DEUX « 1 % », deux
    // roles differents pour le meme chiffre. C'est deja ce que rend cette
    // fonction : l'ecran ne le REFORMATE PAS.
    pourcent: (() => {
      if (POIDS_TOTAL <= 0 || poids <= 0) return 0;
      const brut = (poids / POIDS_TOTAL) * 100;
      return brut < 10 ? Math.round(brut * 10) / 10 : Math.round(brut);
    })(),
    poids,
    total: POIDS_TOTAL,
    comptees: connues.length,
    sensibles: connues.filter((c) => SENSIBLES.includes(c)),
    parNiveau,
  };
}

/**
 * « accordees / total » pour une categorie. PURE.
 *
 * ⚠️ LE TOTAL EST CELUI DE LA TABLE, PAS DU ROLE. Compter le total sur les
 * seules permissions cochees afficherait « 3 / 3 » pour un role qui n'en tient
 * que trois sur onze — c'est-a-dire le contraire de l'information cherchee.
 */
export function compteDeLaCategorie(cle, jeu) {
  const dedans = permissionsDeLaCategorie(cle);
  const tenu = new Set(jeuDuRole(jeu));
  return {
    accordees: dedans.filter((p) => tenu.has(p.cle)).length,
    total: dedans.length,
    sensibles: dedans.filter((p) => p.sensible).length,
  };
}

/**
 * TOUT VALIDER — pour une categorie (`categorie`) ou pour le role entier.
 *
 * ⚠️ POUR QUI N'EST PAS PROPRIETAIRE, LES QUATRE SENSIBLES SONT ECARTEES.
 * C'est le geste par lequel l'escalade rentrerait par la fenetre apres avoir
 * ete fermee six fois cote serveur : un « tout valider » qui coche
 * `service.admin` donnerait la maison a qui l'a clique. Le serveur repondrait
 * 403 de toute facon — mais un ecran qui envoie une requete vouee au 403 fait
 * passer une REGLE pour une PANNE, et on la cherche du mauvais cote pendant
 * une heure.
 *
 * Rend `{ permissions, ecartees }` : la liste a envoyer, et les sensibles
 * laissees de cote pour que l'ecran puisse LE DIRE en une ligne.
 */
export function toutValider(jeu, { categorie = null, proprietaire = false } = {}) {
  const tenu = new Set(jeuDuRole(jeu));
  const visees = categorie ? permissionsDeLaCategorie(categorie) : PERMISSIONS;
  const ecartees = [];
  for (const p of visees) {
    if (p.sensible && !proprietaire) {
      // Une sensible DEJA accordee n'est pas retiree par ce geste : « tout
      // valider » n'a jamais voulu dire « et retire ce que tu ne peux pas
      // donner ». On la signale seulement si elle manque encore.
      if (!tenu.has(p.cle)) ecartees.push(p.cle);
      continue;
    }
    tenu.add(p.cle);
  }
  return { permissions: jeuDuRole([...tenu]), ecartees };
}

/**
 * TOUT REFUSER — pour une categorie ou pour le role entier.
 *
 * ⚠️ IL NE TOUCHE QUE LA CATEGORIE VISEE. Vider le role entier quand on a
 * clique sur « Les liens » retirerait des droits a des gens sans que rien ne
 * l'ait demande.
 *
 * ⚠️ ET UN NON-PROPRIETAIRE NE RETIRE PAS DAVANTAGE UNE SENSIBLE QU'IL NE
 * L'ACCORDE. Le serveur le refuse par la meme porte (« supprimer un pouvoir
 * est un geste sur ce pouvoir », DELETE /api/admin/roles/:id) : sans cette
 * symetrie, un administrateur non proprietaire pouvait DEPOUILLER les autres de
 * `service.admin` et rester seul a le porter. Le degat n'est pas une elevation,
 * c'est une eviction — et rien ne l'arretait.
 */
export function toutRefuser(jeu, { categorie = null, proprietaire = false } = {}) {
  const tenu = new Set(jeuDuRole(jeu));
  const visees = categorie ? permissionsDeLaCategorie(categorie) : PERMISSIONS;
  const ecartees = [];
  for (const p of visees) {
    if (p.sensible && !proprietaire) {
      if (tenu.has(p.cle)) ecartees.push(p.cle);
      continue;
    }
    tenu.delete(p.cle);
  }
  return { permissions: jeuDuRole([...tenu]), ecartees };
}

/**
 * La ligne qui dit ce que le geste n'a PAS fait, et pourquoi.
 *
 * ⚠️ SANS ELLE, « tout valider » MENTIRAIT PAR OMISSION : les cases se
 * cocheraient partout sauf quatre, et rien ne dirait que c'est voulu.
 */
export function motDesSensiblesEcartees(cles, { geste = 'accorder' } = {}) {
  const noms = (cles ?? []).map((c) => laPermission(c)?.nom ?? String(c));
  if (!noms.length) return '';
  const s = noms.length > 1 ? 's' : '';
  return `${noms.length} permission${s} sensible${s} laissée${s} de côté (${noms.join(', ')}) : `
    + `seul un propriétaire peut les ${geste}. Tout le reste a été appliqué.`;
}

/**
 * De quoi peindre la barre de pouvoir — et RIEN qui se recalcule.
 *
 * ⚠️ `pourcent` N'EST PAS REFORMATE : une decimale sous 10 %, un entier
 * au-dessus, c'est deja ce que rend `niveauDePouvoir`. La seule chose ajoutee
 * est `largeur`, et c'est une mesure d'AFFICHAGE, pas un chiffre : une barre de
 * 0,5 % de large serait invisible et ferait passer « 0,5 % » pour « 0 % » — le
 * nombre ecrit a cote, lui, reste exactement celui de la fonction.
 */
export function barreDePouvoir(jeu) {
  const mesure = niveauDePouvoir(jeu);
  return {
    ...mesure,
    texte: `${String(mesure.pourcent).replace('.', ',')} %`,
    largeur: mesure.poids > 0 ? Math.max(mesure.pourcent, 1.5) : 0,
  };
}

// ---------------------------------------------------------------------------
// LE PROPRIETAIRE N'EST PAS UN INVITE
// ---------------------------------------------------------------------------
// Toji : « dans les personnes invitees, je suis toujours dedans ».
//
// ⚠️ ET IL NE DOIT PAS DISPARAITRE POUR AUTANT. Le retirer de l'ecran serait
// l'autre facon de se tromper : on ne saurait plus qui tient la maison, ni
// combien ils sont. Il est donc SORTI de la liste et REMIS a sa place, en tete,
// discretement — sans menu de role et sans « retirer l'acces », parce qu'aucun
// des deux ne veut dire quoi que ce soit sur lui.
// ---------------------------------------------------------------------------

/**
 * Separe les proprietaires du reste. PURE.
 *
 * `proprietaires` arrive de `GET /api/admin/invites` (l'autre lot) et peut
 * porter soit des entrees completes, soit de simples identifiants.
 *
 * ⚠️ LE REPLI N'EST PAS DU CONFORT : tant que la route ne rend pas encore la
 * cle, on reconnait les proprietaires a leur PROVENANCE (`type: 'proprietaire'`,
 * la meme cle que la pastille). Sans lui, l'ecran d'aujourd'hui remettrait Toji
 * dans la liste des invites — c'est-a-dire exactement le defaut qu'on repare.
 */
export function separerLesPersonnes({ invites = [], proprietaires = [] } = {}) {
  const liste = Array.isArray(invites) ? invites : [];
  const bruts = Array.isArray(proprietaires) ? proprietaires : [];
  const idDe = (v) => String((v && typeof v === 'object' ? v.id : v) ?? '').trim();
  const annonces = [...new Set(bruts.map(idDe).filter(Boolean))];
  const devines = liste
    .filter((e) => (e?.provenances ?? []).some((p) => p?.type === 'proprietaire'))
    .map((e) => String(e?.id ?? '').trim())
    .filter(Boolean);
  const ids = new Set(annonces.length ? annonces : devines);

  const chefs = [...ids].map((id) => {
    const dansLaListe = liste.find((e) => String(e?.id ?? '') === id) ?? null;
    const annonce = bruts.find((v) => idDe(v) === id);
    // L'entree de la liste porte le profil resolu (pseudo, avatar, droits) ;
    // celle de `proprietaires` peut n'etre qu'un identifiant. On garde les deux,
    // l'annonce ayant le dernier mot sur ce qu'elle dit vraiment.
    return {
      ...(dansLaListe ?? {}),
      ...(annonce && typeof annonce === 'object' ? annonce : {}),
      id,
      proprietaire: true,
    };
  });

  return {
    proprietaires: chefs,
    personnes: liste.filter((e) => !ids.has(String(e?.id ?? ''))),
  };
}

// ---------------------------------------------------------------------------
// LES INVITATIONS PAR LIEN
// ---------------------------------------------------------------------------

/**
 * JJ/MM/AAAA, calcule en UTC — comme `invites.js`, et pour la meme raison :
 * `toLocaleDateString` rendrait une chaine differente selon le fuseau de la
 * machine, donc deux dates pour la meme invitation selon qui regarde.
 */
export function leJour(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return null;
  const d = new Date(n);
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
}

/**
 * Une invitation, ramenee a une forme que l'ecran sait lire. PURE.
 *
 * ⚠️ LES SYNONYMES SONT VOLONTAIRES ET BORNES. La route d'invitation vient d'un
 * autre lot : accepter deux ou trois orthographes connues (`maximum`/`max`,
 * `expire`/`echeance`) coute trois lignes, alors qu'un champ mal devine
 * afficherait « 0 / 0 » avec aplomb sur une invitation vivante. Ce qui n'est
 * PAS devine, c'est l'absence : `maximum: null` veut dire « sans plafond », et
 * c'est ecrit comme tel — jamais zero, qui voudrait dire « epuisee ».
 */
export function normaliserInvitation(brut) {
  const entier = (v) => { const x = Number(v); return Number.isFinite(x) && x >= 0 ? Math.floor(x) : null; };
  const date = (v) => { const x = Number(v); return Number.isFinite(x) && x > 0 ? x : null; };
  const roles = brut?.roles ?? brut?.rolesMaison ?? [];
  return {
    id: String(brut?.id ?? '').trim(),
    roles: (Array.isArray(roles) ? roles : [roles])
      .map((r) => String(r?.id ?? r ?? '').trim()).filter(Boolean),
    usages: entier(brut?.usages ?? brut?.utilisee ?? brut?.poses) ?? 0,
    maximum: entier(brut?.maximum ?? brut?.max ?? brut?.combien),
    expire: date(brut?.expire ?? brut?.expireLe ?? brut?.echeance),
    cree: date(brut?.cree ?? brut?.creeLe),
    par: String(brut?.par ?? brut?.createur ?? '').trim(),
    note: String(brut?.note ?? '').trim(),
  };
}

/**
 * Ce qu'une invitation vaut MAINTENANT — et le mot qui le dit.
 *
 * ⚠️ EPUISEE ET EXPIREE SE LISENT, ELLES NE SE DEVINENT PAS. Une invitation
 * morte qui s'affiche comme les autres se recopie et se donne : la personne
 * clique, se fait refuser, et revient demander pourquoi « le lien ne marche
 * pas ». Les deux causes sont distinctes — l'une se repare en relevant le
 * plafond, l'autre en repoussant l'echeance — donc elles se disent separement,
 * et toutes les deux quand elles sont vraies toutes les deux.
 */
export function etatDeLInvitation(brut, maintenant = Date.now()) {
  const i = normaliserInvitation(brut);
  const epuisee = i.maximum !== null && i.usages >= i.maximum;
  const expiree = i.expire !== null && i.expire <= maintenant;
  const usages = i.maximum === null
    ? `${i.usages} usage${i.usages > 1 ? 's' : ''} — sans plafond`
    : `${i.usages} / ${i.maximum}`;
  const jour = leJour(i.expire);
  const echeance = jour ? (expiree ? `expirée le ${jour}` : `jusqu’au ${jour}`) : 'sans échéance';
  const mot = epuisee && expiree
    ? `épuisée (${usages}) et ${echeance}`
    : epuisee
      ? `épuisée — ${usages}`
      : expiree
        ? `${echeance} — ${usages}`
        : `valable — ${usages}, ${echeance}`;
  return {
    ...i, epuisee, expiree, utilisable: !epuisee && !expiree, usagesLisibles: usages, echeance, mot,
  };
}

// ---------------------------------------------------------------------------
// LES AIDES DE LA LISTE DES PERSONNES
// ---------------------------------------------------------------------------

/** Un identifiant Discord — dix-sept a vingt chiffres, comme cote serveur. */
export const EST_UN_FLOCON = (v) => /^\d{17,20}$/.test(String(v ?? ''));

/**
 * Une source de droit, en clair.
 *
 * `origines` rend `{ type: 'proprietaire'|'discord'|'maison'|'heritage', id, nom }` :
 * le TYPE est ce qui compte a l'ecran, parce que « role Discord Moderateur » et
 * « role maison Moderateur » se reglent a deux endroits differents. Les
 * confondre ferait chercher dans le mauvais etage.
 */
export function etiquetteSource(source) {
  const nom = String(source?.nom ?? source?.id ?? '').trim();
  if (source?.type === 'proprietaire') return 'propriétaire de la maison';
  if (source?.type === 'discord') return `rôle Discord ${nom || 'sans nom'}`;
  if (source?.type === 'maison') return `rôle maison ${nom || 'sans nom'}`;
  // ⚠️ L'HERITAGE — une liaison role Discord → role maison. On dit les DEUX
  // bouts de la chaine, parce que retirer ce droit se fait a l'un ou a l'autre :
  // delier le role Discord, ou retirer la permission du role maison.
  if (source?.type === 'heritage') {
    const via = String(source?.via ?? source?.role ?? '').trim();
    return via
      ? `rôle maison ${nom || 'sans nom'}, hérité du rôle Discord ${via}`
      : `rôle maison ${nom || 'sans nom'}, hérité d’un rôle Discord`;
  }
  return nom ? `origine inconnue (${nom})` : 'origine inconnue';
}

/**
 * L'infobulle d'une pastille : le droit, puis d'OU il vient.
 *
 * Toji ne demande pas « a-t-il le droit ? » mais « pourquoi l'a-t-il ? » — la
 * reponse est la seule qui permette de le lui retirer au bon endroit.
 */
export function titreDuDroit(colonne, origines) {
  const sources = Array.isArray(origines?.[colonne.cle]) ? origines[colonne.cle] : [];
  if (!sources.length) return `${colonne.nom} — non accordé`;
  return `${colonne.nom} — accordé par : ${sources.map(etiquetteSource).join(', ')}`;
}

/**
 * La lettre d'un droit — ce qui tient dans un rond de 24 px.
 *
 * ⚠️ ELLE VIENT DE LA CLE, PAS DU NOM. Les quatre noms commencent par des mots
 * qui se ressemblent une fois tronques (« Lancer une lecture », « Modifier les
 * visuels », « Gerer les add-ons », « Section admin » → LA MO GE SE, illisible)
 * alors que les quatre cles donnent L P S A, distinctes du premier coup d'oeil.
 */
export function sigle(colonne) {
  return String(colonne?.cle ?? colonne?.nom ?? '?').trim().slice(0, 1).toUpperCase();
}

/**
 * Les roles maison d'une personne, NOMMES.
 *
 * ⚠️ UNE REFERENCE VERS UN ROLE DISPARU N'ACCORDE RIEN (regle 4 de
 * `droitsDe`), et elle ne doit donc rien afficher non plus : montrer
 * « r-3f2a… » a cote de quelqu'un laisserait croire a un role qui agit encore.
 */
export function rolesNommes(ids, roles) {
  const table = new Map((roles ?? []).map((r) => [r.id, r]));
  return (ids ?? []).map((id) => table.get(id)).filter(Boolean);
}

/**
 * « 3 personnes », « 1 personne », « personne » — pour la confirmation.
 *
 * ⚠️ IL SE COMPTE SUR LA LISTE UNIQUE. Compte sur l'ancienne liste des
 * « membres » (le seul mapping maison), il ignorait quiconque porte le role
 * sans avoir de ligne a lui — et annoncait « aucun membre » avant de retirer
 * ses droits a quelqu'un.
 */
export function compteDeMembres(roleId, personnes) {
  const n = (personnes ?? []).filter((m) => (m.roles ?? []).includes(roleId)).length;
  if (n === 0) return 'personne';
  return `${n} personne${n > 1 ? 's' : ''}`;
}

// ---------------------------------------------------------------------------
// D'OU VIENNENT LES GENS
// ---------------------------------------------------------------------------
// ⚠️ CE MODULE N'IMPORTE RIEN (voir l'en-tete). La table des provenances et la
// fabrication d'une adresse d'avatar sont donc REDITES ici, cote ecran, plutot
// que tirees de `invites.js` et `profils-discord.js` — qui vivent dans le
// processus serveur, lisent le disque et appellent Discord. Ce qui lie les deux
// cotes, c'est la cle `type` : les sept memes (`proprietaire`, `invite`,
// `membre`, `session`, `octroi`, `source`, `demande`), surveillees nom par nom
// par `essais/invites-ecran.mjs`.

/**
 * Les provenances en clair, ET DANS UN ORDRE FIXE.
 *
 * « Pourquoi celle-la est-elle la ? » est la premiere question devant la
 * liste : une meme personne peut y etre par trois chemins a la fois et les
 * trois se coupent a trois endroits differents.
 *
 * ⚠️ `ordre` N'EST PAS DECORATIF. Trie sur l'ordre d'arrivee du serveur, le
 * meme jeu de provenances sortirait dans deux ordres differents sur deux lignes
 * voisines, et l'oeil ne pourrait plus comparer deux lignes d'un coup.
 *
 * ⚠️ LES RANGS SONT CEUX DE `PROVENANCES` (invites.js), AU CHIFFRE PRES. Deux
 * tables qui se lisent par la meme cle et rangeraient les memes provenances
 * autrement, c'est une divergence qui ne leve jamais.
 */
export const PROVENANCES_LISIBLES = {
  proprietaire: { libelle: 'propriétaire', ordre: 0 },
  invite: { libelle: 'invité', ordre: 1 },
  membre: { libelle: 'membre réglé', ordre: 2 },
  session: { libelle: 'connecté', ordre: 3 },
  octroi: { libelle: 'octroi reçu', ordre: 4 },
  source: { libelle: 'source prêtée', ordre: 5 },
  demande: { libelle: 'demande acceptée', ordre: 6 },
};

/**
 * Le mot d'une provenance.
 *
 * ⚠️ UN TYPE INCONNU SE MONTRE BRUT, IL NE DISPARAIT PAS. Le serveur peut en
 * ajouter un avant l'ecran ; une pastille au mot moche vaut mieux qu'une
 * personne dont la presence ne s'explique plus.
 */
export function libelleProvenance(type) {
  const cle = String(type ?? '').trim();
  return PROVENANCES_LISIBLES[cle]?.libelle ?? (cle || 'provenance inconnue');
}

/**
 * Les pastilles d'une ligne : dedoublonnees par type, triees par `ordre`.
 *
 * Deux fois la meme provenance (deux sources pretees, par exemple) ne fait
 * qu'une pastille — sinon une personne qui a prete cinq comptes occuperait
 * toute la largeur de sa ligne. Le detail le plus renseigne des deux est celui
 * qu'on garde : il part dans l'infobulle.
 */
export function pastillesDeProvenance(provenances) {
  const vues = new Map();
  for (const p of Array.isArray(provenances) ? provenances : []) {
    const type = String(p?.type ?? '').trim();
    if (!type) continue;
    const detail = String(p?.detail ?? '').trim();
    const deja = vues.get(type);
    if (deja && !(detail && !deja.detail)) continue;
    vues.set(type, { type, libelle: libelleProvenance(type), detail });
  }
  const rang = (t) => PROVENANCES_LISIBLES[t]?.ordre ?? 99;
  return [...vues.values()]
    .sort((a, b) => rang(a.type) - rang(b.type) || a.type.localeCompare(b.type));
}

/**
 * L'adresse de la photo de profil — ou `null`, qui vaut « pose un monogramme ».
 *
 * ⚠️ L'EXTENSION SE LIT DANS LE HASH, ELLE NE SE DEVINE PAS. Discord prefixe
 * `a_` les avatars animes : demander `.png` sur l'un d'eux rend une image FIXE,
 * et `.gif` sur un avatar ordinaire rend 404 — donc l'icone cassee du
 * navigateur au milieu de la liste.
 *
 * ⚠️ PAS D'AVATAR PAR DEFAUT. Quand on ne connait pas le hash, on ne sert PAS
 * le rond gris generique de Discord : il ferait croire que c'est le sien.
 *
 * ⚠️ ET LE SERVEUR N'ENVOIE JAMAIS `null` — C'EST LE PIEGE. `urlAvatar()`
 * (profils-discord.js) « rend TOUJOURS une adresse utilisable » : sans hash,
 * elle sert `/embed/avatars/<n>.png`, c'est-a-dire precisement le rond
 * generique. On le reconnait et on retombe sur le monogramme.
 */
export function urlDeLAvatar(id, avatar, { taille = 64 } = {}) {
  const hash = String(avatar ?? '').trim();
  if (!hash) return null;
  if (/^https?:\/\//i.test(hash)) return /\/embed\/avatars\//i.test(hash) ? null : hash;
  const ext = hash.startsWith('a_') ? 'gif' : 'png';
  const ou = encodeURIComponent(String(id ?? ''));
  return `https://cdn.discordapp.com/avatars/${ou}/${encodeURIComponent(hash)}.${ext}?size=${taille}`;
}

/**
 * De qui parle cette ligne — pseudo general d'abord, `@compte` en dessous.
 *
 * ⚠️ PAS D'UI FAUSSE QUAND DISCORD N'A PAS REPONDU. `profils.manquants` porte
 * les identifiants que la resolution n'a pas pu lire : ces lignes-la affichent
 * l'identifiant BRUT et le disent franchement. Poser « Inconnu » et un rond
 * gris ferait passer un trou pour une donnee.
 */
export function identiteAffichee(entree, manquants) {
  const id = String(entree?.id ?? '').trim();
  const absent = new Set((manquants ?? []).map((x) => String(x)));
  const pseudo = String(entree?.pseudo ?? '').trim();
  const compte = String(entree?.compte ?? '').trim();
  if (absent.has(id) || !pseudo || pseudo === id) {
    return {
      id,
      inconnu: true,
      titre: id || 'identifiant manquant',
      sous: 'Discord n\'a pas répondu — identifiant brut',
      initiale: '?',
      avatar: null,
    };
  }
  return {
    id,
    inconnu: false,
    titre: pseudo,
    // L'identifiant reste visible quand il n'y a pas de nom de compte : c'est
    // lui qu'on recopie pour verifier quoi que ce soit ailleurs.
    sous: compte ? `@${compte}` : id,
    // ⚠️ `[...pseudo][0]`, PAS `pseudo[0]` : un pseudo qui commence par un
    // emoji est une paire de substituts, et `[0]` en rendrait la moitie —
    // c'est-a-dire un carre blanc.
    initiale: ([...pseudo][0] ?? '?').toUpperCase(),
    avatar: urlDeLAvatar(id, entree?.avatar, { taille: 64 }),
  };
}

/**
 * La liste des roles d'une personne, le role pose en plus — SANS doublon.
 *
 * ⚠️ UN DOUBLON N'EST PAS INOFFENSIF : il afficherait deux fois la meme
 * pastille sur la ligne, et `compteDeMembres` compterait la personne deux fois
 * dans la confirmation de suppression du role.
 */
export function avecLeRole(roles, roleId) {
  const id = String(roleId ?? '').trim();
  const liste = (roles ?? []).map((r) => String(r));
  if (!id || liste.includes(id)) return liste;
  return [...liste, id];
}

/**
 * La personne vient d'etre reglee a la main : elle figure desormais dans
 * `acces.membres`, donc elle porte la provenance `membre`.
 *
 * On l'ajoute nous-memes plutot que de relire toute la liste : c'est l'ecriture
 * qu'on vient de faire, pas une supposition. Une seule fois, evidemment.
 */
export function marquerRegle(entree) {
  const deja = (entree?.provenances ?? []).some((p) => p?.type === 'membre');
  return {
    ...entree,
    pose: true,
    provenances: deja
      ? (entree?.provenances ?? [])
      : [...(entree?.provenances ?? []), { type: 'membre', detail: '' }],
  };
}

/**
 * Ce que la liste dit d'elle-meme sous le bloc.
 *
 * ⚠️ LES PROFILS NON RESOLUS SE COMPTENT A VOIX HAUTE. « 24 personnes » tout
 * court laisserait croire que les vingt-quatre sont lisibles, alors que trois
 * lignes n'affichent qu'un identifiant : le nombre manquant est ce qui dit
 * qu'il y a une panne Discord a regarder, et non des lignes mal faites.
 */
export function resumeDesProfils({ total = 0, resolus = 0, manquants = 0, proprietaires = 0 } = {}) {
  if (!total && !proprietaires) return 'personne pour l\'instant';
  const s = (n) => (n > 1 ? 's' : '');
  // ⚠️ LES PROPRIETAIRES SE COMPTENT A PART, PARCE QU'ILS SONT AFFICHES A PART.
  // « 12 personnes » sous une liste de onze lignes plus un encadre en tete
  // enverrait chercher la douzieme ligne manquante.
  const chefs = proprietaires
    ? `${proprietaires} propriétaire${s(proprietaires)} + ` : '';
  const gens = `${chefs}${total} personne${s(total)}`;
  if (!manquants) return `${gens} — ${resolus} profil${s(resolus)} lu${s(resolus)} chez Discord`;
  return `${gens} — ${manquants} identifiant${s(manquants)} que Discord n'a pas résolu${s(manquants)}, `
    + `affiché${s(manquants)} brut${s(manquants)}`;
}

// ---------------------------------------------------------------------------
// L'ECRAN
// ---------------------------------------------------------------------------
/**
 * Monte l'ecran d'administration et rend de quoi l'ouvrir et le fermer.
 *
 * Les outils viennent de main.js : ce module n'importe rien du lecteur, et un
 * banc peut donc le charger en lui donnant des figurations.
 */
export function monterEcranAdmin({
  $, esc, texte, api, vue, surClic, tracer,
  // ⚠️ LA LOGIQUE DES POINTS ARRIVE PAR ICI, ELLE NE S'IMPORTE PAS.
  // Ce module n'importe rien (voir l'encadré du haut) ; `src/points.js`, lui,
  // a le droit d'importer les deux fichiers que le BOT exécute pour de vrai —
  // les bornes des réglages et le calcul d'un prix. C'est ce qui garantit que
  // l'aperçu affiché ici est exactement ce qui sera débité là-bas.
  points = null,
  // Le bloc « serveurs + roles » demenage depuis les reglages : il faut donc
  // pouvoir refermer la pop-up qui le contient parfois, et le repeindre une
  // fois pose ici.
  ouvrirModale, fermerModale, chargerAcces, lireServeursAdmin,
  // ⚠️ LES BLOCS DEMENAGES ONT BESOIN QU'ON LES PREPARE, ET PERSONNE NE LE
  // FAISAIT PLUS. Onze `preparer*`/`charger*` vivaient dans `ouvrirModale()`,
  // la pop-up des reglages personnels, du temps ou l'administration y logeait.
  // Depuis le 16/09 ils vivent ici : sans cet appel, la section « Sources et
  // add-ons » s'ouvre a zero pixel de haut — `#bloc-addons` porte `hidden` dans
  // le balisage, et c'est `chargerAddons()` qui le demasque.
  preparerLAdministration = null,
  // Le serveur Discord affiche dans le bloc de liaison, quand il y en a un.
  guildCourant = () => null,
} = {}) {
  // --- ce que le serveur a resolu, jamais ce qu'on a deduit ---
  let colonnes = [];
  let roles = [];
  let personnes = [];
  let chefs = [];
  let manquants = [];
  let panneListe = null;
  // ⚠️ QUI REGARDE, ET EST-CE UN PROPRIETAIRE. `GET /api/moi` (auth seul, deja
  // servie a chaque ouverture de page) rend `categorie: 'proprietaire'|'invite'`.
  // Sans cette reponse, « tout valider » ne peut pas savoir s'il a le droit de
  // cocher les quatre sensibles — et la seule valeur sure par defaut est NON.
  let jeSuisProprietaire = false;
  let pourquoiPasProprietaire = '';
  let moiId = '';

  // --- l'etat de l'ecran ---
  let enEdition = null;            // la ligne dont la fiche est depliee
  let roleOuvert = null;           // le role regle dans la matrice
  let categorieOuverte = CATEGORIES[0]?.cle ?? null;
  let vueAvant = 'v-reglages';
  let modaleAvant = false;

  // --- la liste du serveur Discord, pour « ajouter par identifiant » ---
  let pseudos = new Map();
  let panneDiscord = null;
  let listeDiscord = null;

  // --- les invitations par lien ---
  let invitations = [];
  let panneInvitations = null;
  // ⚠️ LE LIEN N'EST MONTRE QU'UNE FOIS. On le garde ici, en memoire de page,
  // le temps de le copier — jamais dans l'etat des invitations relues, parce
  // que le serveur ne le rendra plus.
  let lienTouNeuf = null;

  // Un mot par ligne, a cote du geste qui l'a provoque : « seul un
  // proprietaire peut accorder admin » se lit la ou l'on vient de cliquer,
  // jamais dans un `alert()` qu'on chasse d'un reflexe sans l'avoir lu.
  const motsDeLigne = new Map();
  const motsMatrice = new Map();

  const direRole = (mot) => texte('adm-roles-etat', mot);
  const direListe = (mot) => texte('adm-personnes-etat', mot);
  const direPoints = (mot) => texte('adm-pts-etat', mot);

  // --- l'économie de séance : le brouillon en cours, et les rôles du serveur ---
  // ⚠️ UN BROUILLON, PAS LES RÉGLAGES. Il garde ce qui est TAPÉ, chaînes vides
  // comprises : normaliser à chaque frappe ferait disparaître sous les doigts
  // la ligne dont on efface le chiffre pour le retaper. Voir `src/points.js`.
  let brouillonPoints = null;
  let rolesPoints = [];

  /** Le mot d'un geste de la matrice — ecrit sur place, sans repeindre. */
  function direMatrice(place, mot) {
    if (mot) motsMatrice.set(place, mot); else motsMatrice.delete(place);
    const zone = $(`adm-mot-${place}`);
    if (zone) zone.textContent = mot ?? '';
  }
  const motDe = (place) => motsMatrice.get(place) ?? '';

  // -------------------------------------------------------------------------
  // IL N'Y A PLUS DE BLOC EMPRUNTE — ET C'EST LE CORRECTIF, PAS UN RANGEMENT.
  // -------------------------------------------------------------------------
  // Deux fonctions vivaient ici : `deplacerLeBlocDiscord`, qui prenait
  // `#acces-discord` dans l'onglet « Acces » du panneau de reglages le temps que
  // cet ecran etait ouvert, et `rendreLeBlocDiscord`, qui le rendait — plus une
  // garde pour le cas ou l'on quittait l'ecran par un autre chemin, plus un
  // `rendreLeBloc` expose a `main.js` pour que `chargerAcces` puisse le
  // rapatrier, plus une garde DANS cette fonction pour qu'elle ne se rende pas
  // le bloc a elle-meme. Cinq pieces, pour une seule cause : le bloc avait DEUX
  // domiciles.
  //
  // Depuis le 16/09 il n'en a plus qu'un — la section « Roles » du tableau de
  // bord — et les cinq pieces ont disparu avec la cause. C'est ce meme
  // dedoublement qui avait donne « en grand, les membres n'apparaissent pas du
  // tout » le 14/09 : `getElementById` rend le PREMIER element du document, pas
  // celui qu'on regarde.

  // =========================================================================
  // SECTION 1 — LES PERSONNES
  // =========================================================================
  // ⚠️ CETTE ZONE EST MONTEE PAR LE CODE, PAS ECRITE DANS `public/index.html`.
  // Elle remplace A ELLE SEULE les deux zones d'avant (« Les personnes
  // invitees » et « Membres ») : la monter ici, en un endroit, evite d'avoir
  // deux editeurs sur le meme fichier pour une seule liste. Elle se pose AVANT
  // `#adm-zone-roles` : « qui est la ? » est la premiere question qu'on pose en
  // ouvrant l'ecran, les roles viennent apres.
  function assurerLaZonePersonnes() {
    const deja = $('adm-zone-personnes');
    if (deja) return deja;
    // ⚠️ LA ZONE VA DANS LA SECTION « ACCES », PAS DANS UNE PAGE UNIQUE.
    //
    // Elle visait `#v-admin .adm-page` — le conteneur unique de l'ancien ecran,
    // ou tout s'empilait. Le tableau de bord du 16/09 n'a plus de page unique :
    // il a neuf sections, et « qui est la » appartient a une seule d'entre
    // elles. Viser l'ancien conteneur rendrait `null` ici, la zone ne serait
    // jamais montee, et la liste des personnes n'existerait nulle part — la
    // panne EXACTE du 14/09, par un autre chemin.
    const page = document.querySelector('#adm-corps .adm-section[data-adm="acces"]');
    if (!page) return null;
    const zone = document.createElement('section');
    zone.className = 'adm-zone';
    zone.id = 'adm-zone-personnes';
    zone.innerHTML = `<div class="adm-zone-tete">
        <p class="titre-section">Les personnes</p>
        <button type="button" id="inv-inviter">Inviter quelqu’un</button>
        <button type="button" class="ghost" id="adm-membre-ajouter">Ajouter par identifiant</button>
        <button type="button" class="ghost" id="inv-relire">Relire</button>
      </div>
      <p class="muted adm-aide">
        Tout le monde qui a un accès, d’où qu’il vienne — c’est la MÊME liste que
        les « membres » : une personne invitée est un membre. Les pastilles
        « pourquoi » disent d’où vient sa présence ; celles de « droits » ce
        qu’elle peut vraiment faire, tout compris — pose le curseur dessus pour
        voir l’origine de chaque droit. Le menu pose un rôle tout de suite ;
        « Modifier » ouvre l’étiquette et la note sur place.
      </p>
      <div id="inv-invitation" hidden></div>
      <div id="adm-ajout" hidden></div>
      <div class="inv-chefs" id="inv-chefs"></div>
      <div class="inv-entete" id="inv-entete">
        <span>Qui</span><span>Pourquoi</span><span>Rôles maison</span>
        <span>Droits</span><span>Ce qu’on lui pose</span>
      </div>
      <div class="inv-liste" id="inv-liste"></div>
      <span class="etat-lang" id="adm-personnes-etat"></span>`;
    // ⚠️ `prepend`, ET PLUS D'ANCRE DU TOUT — C'EST LE CORRECTIF DE FOND.
    //
    // Ce qui vivait ici : `page.insertBefore(zone, $('adm-zone-roles')
    // ?.closest('#v-admin .adm-page > *') ?? null)`, plus un `try/catch`, plus
    // trente lignes pour expliquer pourquoi. La raison : la pre-insertion du DOM
    // Standard leve `NotFoundError` des que l'ancre n'est pas un enfant DIRECT
    // du parent, et `#adm-zone-roles` vivait deux etages plus bas. La levee se
    // produisait AVANT le `try` de `chargerLesPersonnes` : `ouvrir()` rejetait,
    // et les quatre derniers gestes — `peindreLesPersonnes`, `peindreLaMatrice`,
    // `chargerListeDiscord`, `chargerAcces` — ne partaient JAMAIS. Ecran ouvert,
    // aucune liste, aucun bouton « Inviter quelqu'un », et pas une erreur
    // visible. C'est la panne du 14/09.
    //
    // Une ancre existe pour dire OU, entre deux freres. Ici il n'y a rien a
    // viser : « qui est la » est la premiere question de la section « Acces »,
    // donc la zone va EN TETE. `prepend` le dit en un mot, ne prend aucune
    // ancre, et ne peut donc pas lever pour cette raison-la. Le defaut n'est pas
    // corrige, il est devenu impossible a ecrire.
    page.prepend(zone);
    surClic('inv-relire', () => chargerLesPersonnes());
    surClic('inv-inviter', () => {
      const bloc = $('inv-invitation');
      if (!bloc) return;
      bloc.hidden = !bloc.hidden;
      if (!bloc.hidden) { peindreInvitations(); chargerInvitations(); }
    });
    surClic('adm-membre-ajouter', () => {
      const bloc = $('adm-ajout');
      if (!bloc) return;
      bloc.hidden = !bloc.hidden;
      if (!bloc.hidden) peindreAjout();
    });
    return zone;
  }

  /** Le visage d'une personne, ou son monogramme. Jamais d'image cassee. */
  function visage(qui, classe = 'inv-face') {
    return qui.avatar
      ? `<img class="${classe}" src="${esc(qui.avatar)}" alt="" data-mono="${esc(qui.initiale)}" />`
      : `<span class="${classe} inv-mono">${esc(qui.initiale)}</span>`;
  }

  function celluleNoms(e) {
    const qui = identiteAffichee(e, manquants);
    const etiquette = String(e?.nom ?? '').trim();
    const note = String(e?.note ?? '').trim();
    return `${visage(qui)}<span class="inv-noms${qui.inconnu ? ' inv-brut' : ''}">
        <strong>${esc(qui.titre)}</strong>
        <span class="inv-compte">${esc(qui.sous)}</span>
        ${etiquette ? `<span class="inv-etiquette">« ${esc(etiquette)} »</span>` : ''}
        ${note ? `<span class="inv-note">${esc(note)}</span>` : ''}
      </span>`;
  }

  function cellulePuces(m) {
    const tenus = rolesNommes(m.roles, roles);
    if (!tenus.length) return '<span class="inv-rien">aucun</span>';
    return `<div class="adm-puces">${tenus.map((r) => `<span class="adm-puce">`
      + `<i class="adm-point"${r.couleur ? ` style="background:${esc(r.couleur)}"` : ''}></i>${esc(r.nom)}</span>`).join('')}</div>`;
  }

  function celluleDroits(m) {
    if (!colonnes.length) return '<span class="inv-rien">—</span>';
    return `<div class="adm-droits">${colonnes.map((c) => {
      const tenu = !!m.droits?.[c.cle];
      return `<span class="adm-pastille${tenu ? ' tenu' : ''}" title="${esc(titreDuDroit(c, m.origines))}">${esc(sigle(c))}</span>`;
    }).join('')}</div>`;
  }

  /**
   * LA CARTE D'UN PROPRIETAIRE — en tete, a part, et sans geste.
   *
   * ⚠️ NI MENU DE ROLE, NI « RETIRER L'ACCES ». Poser un role sur un
   * proprietaire n'ajoute rien (il a deja tout, par definition), et lui retirer
   * l'acces depuis cet ecran serait la seule facon de se fermer la porte au nez.
   */
  function carteProprietaire(e) {
    const qui = identiteAffichee(e, manquants);
    const cestMoi = moiId && String(e.id) === moiId;
    return `<article class="inv-chef" data-chef="${esc(e.id)}">
        ${visage(qui, 'inv-face')}
        <span class="inv-noms${qui.inconnu ? ' inv-brut' : ''}">
          <strong>${esc(qui.titre)}</strong><span class="inv-compte">${esc(qui.sous)}</span></span>
        <span class="adm-proprio">propriétaire${cestMoi ? ' · toi' : ''}</span>
        <span class="inv-chef-dit">tout, sans exception — ce n’est pas un rôle, c’est la maison</span>
      </article>`;
  }

  /** La ligne d'une personne : qui, d'ou elle vient, ce qu'elle a, ce qu'on pose. */
  function ligneInvite(e) {
    // ⚠️ UNE LIGNE DEJA GAGNEE NE SE REPROPOSE PAS. Choisir un role que la
    // personne porte deja serait un geste sans effet, et le menu qui revient a
    // sa ligne de depart donnerait l'impression que le clic s'est perdu.
    const options = roles.map((r) => {
      const deja = (e.roles ?? []).includes(r.id);
      return `<option value="${esc(r.id)}"${deja ? ' disabled' : ''}>${esc(r.nom)}${deja ? ' — déjà posé' : ''}</option>`;
    }).join('');
    const menu = `<select class="inv-choix" data-choix="1"${roles.length ? '' : ' disabled'}>
        <option value="">${roles.length ? 'Poser un rôle…' : 'aucun rôle maison'}</option>${options}
      </select>`;

    const venues = pastillesDeProvenance(e.provenances);
    const venuesHtml = venues.length
      ? `<div class="inv-venues">${venues.map((p) => `<span class="inv-venue" title="${
        esc(p.detail ? `${p.libelle} — ${p.detail}` : p.libelle)}">${esc(p.libelle)}</span>`).join('')}</div>`
      : '<span class="inv-rien">provenance inconnue</span>';

    return `<article class="inv-ligne" data-invite="${esc(e.id)}">
        <div class="inv-qui">${celluleNoms(e)}</div>
        <div class="inv-venue-cel">${venuesHtml}</div>
        <div class="inv-roles-cel">${cellulePuces(e)}</div>
        <div class="inv-droits-cel">${celluleDroits(e)}</div>
        <div class="inv-geste">
          <div class="inv-gestes">${menu}
            <button type="button" class="ghost" data-editer="1">Modifier</button>
            <button type="button" class="ghost inv-couper" data-couper="1">Retirer l’accès</button></div>
          <span class="inv-etat">${esc(motsDeLigne.get(e.id) ?? '')}</span>
        </div>
        ${enEdition === e.id ? ficheEnLigne(e) : ''}
      </article>`;
  }

  /**
   * L'ETIQUETTE, LA NOTE ET LES ROLES — EN LIGNE, sous la personne.
   *
   * ⚠️ C'EST CE QUE LA ZONE « MEMBRES » SAVAIT FAIRE, ET RIEN N'EN EST PERDU.
   * Elle portait un tableau a part avec sa propre fiche d'edition ; la fiche
   * descend ici, dans la seule liste qui existe. On voit encore la personne
   * qu'on modifie et celles d'a cote.
   */
  /**
   * L'échéance de l'accord « regarder sans vocal », pour un `datetime-local`
   * (heure locale), lue dans les ORIGINES que le serveur a résolues — un
   * accord expiré n'y figure plus, le champ revient donc vide de lui-même.
   */
  function echeanceSansVocal(m) {
    const jusqu = (m.origines?.['lecture.sans_vocal'] ?? []).find((o) => o.type === 'accord')?.jusqu;
    if (!Number.isFinite(jusqu)) return '';
    const d = new Date(jusqu);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  }

  function ficheEnLigne(m) {
    const cases = roles.map((r) => `<label class="adm-case">`
      + `<input type="checkbox" data-fiche-role="${esc(r.id)}"${(m.roles ?? []).includes(r.id) ? ' checked' : ''} />`
      + `${esc(r.nom)}</label>`).join('');
    return `<div class="inv-fiche">
      <div class="adm-fiche-grille">
        <label class="adm-champ"><span>Étiquette</span>
          <input type="text" data-f-nom="1" value="${esc(m.nom ?? '')}" placeholder="Marc, le cousin de…" /></label>
        <label class="adm-champ"><span>Note</span>
          <input type="text" data-f-note="1" value="${esc(m.note ?? '')}" placeholder="pourquoi cette personne est là" /></label>
        <label class="adm-champ"><span>Regarder sans vocal jusqu’au</span>
          <input type="datetime-local" data-f-sans-vocal="1" value="${esc(echeanceSansVocal(m))}" title="vide = pas d’accord ; par rôle : la permission « Regarder sans être en vocal » de la matrice" /></label>
        <div class="adm-champ"><span>Rôles maison</span>
          <div class="adm-role-cases">${cases || '<span class="inv-rien">aucun rôle maison — crée-en un dans « Rôles et permissions »</span>'}</div></div>
      </div>
      <div class="adm-fiche-pied">
        <button type="button" data-f-garder="1">Enregistrer</button>
        <button type="button" class="ghost" data-f-annuler="1">Annuler</button>
        <button type="button" class="ghost adm-pousse" data-f-derouler="1">Retirer ses rôles maison</button>
      </div>
      <p class="muted adm-aide adm-aide-serree">
        « Retirer ses rôles maison » lui enlève ce qu’on lui a posé ici ; son
        accès au lecteur, lui, ne bouge pas (il vient des invités nommés et des
        serveurs). C’est « Retirer l’accès », au-dessus, qui ferme la porte.
      </p>
    </div>`;
  }

  /** La ligne d'un identifiant donne, telle qu'elle est dans le document. */
  function noeudDeLaLigne(id) {
    const liste = $('inv-liste');
    // Comparaison sur `dataset` plutot qu'un selecteur : un identifiant Discord
    // est fait de chiffres, mais un selecteur construit a la main sur une
    // donnee venue du serveur est une habitude qui finit par couter cher.
    return [...(liste?.children ?? [])].find((n) => n.dataset?.invite === id) ?? null;
  }

  function peindreLesPersonnes() {
    const zone = assurerLaZonePersonnes();
    const liste = $('inv-liste');
    if (!zone || !liste) return;

    const bloc = $('inv-chefs');
    if (bloc) {
      bloc.innerHTML = chefs.map(carteProprietaire).join('');
      bloc.hidden = chefs.length === 0;
      for (const el of bloc.children) brancherLesVisages(el);
    }

    const entete = $('inv-entete');
    if (entete) entete.hidden = personnes.length === 0;

    if (!personnes.length) {
      // ⚠️ ON N'AFFIRME PAS « personne » QUAND ON N'A RIEN PU LIRE. Redis muet,
      // Discord muet, route en 500 : la liste est vide pour une raison qui ne
      // parle PAS des acces, et c'est cette raison-la qu'il faut lire.
      liste.innerHTML = panneListe
        ? `<p class="adm-panne">La liste des personnes n’a pas pu être lue :
           ${esc(panneListe)}<br />Ce n’est PAS « personne » — les accès n’ont pas
           changé, c’est la lecture qui a échoué. « Relire » retente.</p>`
        : `<p class="inv-vide">Personne d’autre que ${chefs.length ? 'les propriétaires' : 'toi'} —
           ni invité nommé, ni session ouverte, ni source prêtée.</p>`;
      return;
    }
    // ⚠️ L'ORDRE VIENT DU SERVEUR (`rassemblerLesInvites`). Le refaire ici
    // donnerait deux tris pour une seule liste, et le jour ou l'un des deux
    // change, l'ecran et le serveur ne parleraient plus de la meme personne
    // quand on dit « la troisieme ligne ».
    liste.innerHTML = personnes.map(ligneInvite).join('');
    for (const el of liste.children) brancherLaLigne(el);
  }

  /**
   * Repeindre UNE ligne, a sa place, sans toucher aux autres.
   *
   * ⚠️ REPEINDRE TOUTE LA LISTE APRES UNE POSE SERAIT UNE REGRESSION D'USAGE :
   * poser un role sur dix personnes a la suite veut dire dix listes qui sautent
   * sous le curseur, et la onzieme ligne n'est plus la ou l'oeil l'attendait.
   */
  function repeindreUneLigne(id) {
    const avant = noeudDeLaLigne(id);
    const e = personnes.find((x) => x.id === id);
    if (!avant || !e) { peindreLesPersonnes(); return; }
    const boite = document.createElement('div');
    boite.innerHTML = ligneInvite(e);
    const apres = boite.firstElementChild;
    if (!apres) return;
    avant.replaceWith(apres);
    brancherLaLigne(apres);
  }

  /** Le mot d'une ligne — ecrit sur place, sans repeindre quoi que ce soit. */
  function direSurLaLigne(id, mot) {
    if (mot) motsDeLigne.set(id, mot); else motsDeLigne.delete(id);
    const zone = noeudDeLaLigne(id)?.querySelector('.inv-etat');
    if (zone) zone.textContent = mot ?? '';
    else repeindreUneLigne(id);
  }

  /**
   * Le role tout neuf rejoint les menus des AUTRES lignes, sans les repeindre.
   *
   * Ajouter une option ne change pas un pixel de la page tant que le menu est
   * ferme — la ou repeindre trente lignes pour une option de plus ferait sauter
   * la liste entiere.
   */
  function offrirLeRolePartout(role) {
    const liste = $('inv-liste');
    if (!liste || !role?.id) return;
    for (const menu of liste.querySelectorAll('select[data-choix]')) {
      if ([...menu.options].some((o) => o.value === role.id)) continue;
      const o = document.createElement('option');
      o.value = role.id;
      o.textContent = role.nom;
      menu.appendChild(o);
      // ⚠️ LE TOUT PREMIER ROLE DEBLOQUE LES MENUS DES AUTRES LIGNES. Sans role
      // maison, ils sont rendus eteints et disent « aucun role maison » ; y
      // ajouter une option sans les rallumer laisserait un menu mort portant un
      // role qu'on ne peut pas choisir.
      if (menu.disabled) {
        menu.disabled = false;
        if (menu.options[0] && menu.options[0].value === '') menu.options[0].textContent = 'Poser un rôle…';
      }
    }
  }

  /**
   * ⚠️ UNE PHOTO QUI NE CHARGE PAS DEVIENT UN MONOGRAMME, PAS L'ICONE CASSEE
   * DU NAVIGATEUR. L'adresse vient de l'annuaire : elle peut dater d'une
   * semaine, et l'avatar avoir change chez Discord entre-temps — le CDN repond
   * alors 404, ce qui n'est pas une erreur de requete et ne se voit donc nulle
   * part sauf a l'ecran. Branche en JS : un `onerror=` ecrit dans la chaine
   * demanderait d'imbriquer trois niveaux de guillemets autour d'une donnee
   * venue du serveur, ce qui est la facon la plus courante de fabriquer une
   * injection sans le vouloir.
   */
  function brancherLesVisages(el) {
    for (const face of el.querySelectorAll('img.inv-face[data-mono]')) {
      face.onerror = () => {
        const mono = document.createElement('span');
        mono.className = 'inv-face inv-mono';
        mono.textContent = face.dataset.mono || '?';
        face.replaceWith(mono);
      };
    }
  }

  function brancherLaLigne(el) {
    const id = el?.dataset?.invite;
    if (!id) return;
    brancherLesVisages(el);

    const menu = el.querySelector('select[data-choix]');
    if (menu) {
      menu.onchange = () => {
        const role = menu.value;
        // ⚠️ LE MENU REVIENT A SA LIGNE DE DEPART TOUT DE SUITE. Laisse sur le
        // role choisi, il affirmerait « ce role est pose » avant meme la
        // reponse — et il l'affirmerait encore apres un 403.
        menu.value = '';
        if (role) poserLeRoleSur(id, { role });
      };
    }

    const editer = el.querySelector('[data-editer]');
    if (editer) {
      editer.onclick = () => {
        const avant = enEdition;
        enEdition = avant === id ? null : id;
        motsDeLigne.delete(id);
        if (avant && avant !== id) repeindreUneLigne(avant);
        repeindreUneLigne(id);
      };
    }

    const couper = el.querySelector('[data-couper]');
    if (couper) couper.onclick = () => retirerLAcces(id);

    const garder = el.querySelector('[data-f-garder]');
    if (garder) garder.onclick = () => enregistrerLaFiche(id, el);
    const annuler = el.querySelector('[data-f-annuler]');
    if (annuler) annuler.onclick = () => { enEdition = null; repeindreUneLigne(id); };
    const derouler = el.querySelector('[data-f-derouler]');
    if (derouler) derouler.onclick = () => retirerLesRoles(id);
  }

  /**
   * LE GESTE PRINCIPAL : poser un role sur quelqu'un, en un aller-retour.
   *
   * `corps` vaut `{ role: '<id existant>' }`. ⚠️ LE « + NOUVEAU ROLE » DE LA
   * LIGNE A DISPARU : Toji, « le nouveau role, il sert a rien, parce que je
   * veux juste APPLIQUER un role ». La creation vit dans la section 2, la ou
   * l'on regle ce que le role ouvre — l'endroit ou l'on sait ce qu'on cree.
   */
  async function poserLeRoleSur(id, corps) {
    const i = personnes.findIndex((x) => x.id === id);
    if (i < 0) return;
    direSurLaLigne(id, 'pose…');
    try {
      const reponse = await api(`/api/admin/invites/${encodeURIComponent(id)}/role`, {
        method: 'POST', body: JSON.stringify(corps),
      });
      appliquerLaPose(id, reponse);
      direSurLaLigne(id, reponse?.cree ? 'rôle créé et posé' : 'rôle posé');
    } catch (err) {
      // ⚠️ LA RAISON DE LA ROUTE, MOT POUR MOT. « Seul un proprietaire peut
      // accorder admin » dit qui peut le faire ; « echec » n'apprend rien.
      direSurLaLigne(id, err.message);
    }
  }

  /** Ce que la reponse du serveur change a l'ecran — et rien de plus. */
  function appliquerLaPose(id, reponse) {
    const i = personnes.findIndex((x) => x.id === id);
    if (i < 0) return;
    if (reponse?.role) {
      const j = roles.findIndex((r) => r.id === reponse.role.id);
      if (j >= 0) roles[j] = reponse.role; else roles.push(reponse.role);
      peindreLaMatrice();
      offrirLeRolePartout(reponse.role);
    }
    const m = reponse?.membre;
    // ⚠️ ON PREND LES CHAMPS UN PAR UN, PAS L'OBJET ENTIER. `membre` ne porte
    // ni `pseudo`, ni `avatar`, ni `provenances` : l'etaler par-dessus l'entree
    // effacerait tout ce que la resolution Discord avait apporte, et la ligne
    // retomberait sur son identifiant brut juste apres une pose reussie.
    personnes[i] = marquerRegle({
      ...personnes[i],
      // Les droits RESOLUS viennent du serveur (`droitsDe`) : on ne les
      // recalcule pas ici, c'est toute la regle de ce fichier.
      roles: m?.roles ?? avecLeRole(personnes[i].roles, reponse?.role?.id),
      droits: m?.droits ?? personnes[i].droits,
      origines: m?.origines ?? personnes[i].origines,
      nom: m?.nom ?? personnes[i].nom,
      note: m?.note ?? personnes[i].note,
    });
    repeindreUneLigne(id);
  }

  /** L'etiquette, la note et les roles coches — en une ecriture. */
  async function enregistrerLaFiche(id, el) {
    const i = personnes.findIndex((x) => x.id === id);
    if (i < 0) return;
    const coches = [...(el?.querySelectorAll('[data-fiche-role]') ?? [])]
      .filter((c) => c.checked).map((c) => c.dataset.ficheRole);
    direSurLaLigne(id, 'enregistrement…');
    try {
      const reponse = await api(`/api/admin/membres/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify({
          nom: el?.querySelector('[data-f-nom]')?.value ?? '',
          note: el?.querySelector('[data-f-note]')?.value ?? '',
          roles: coches,
          // Vide = l'accord est retiré ; sinon l'échéance, en ms.
          accords: { 'lecture.sans_vocal': Date.parse(el?.querySelector('[data-f-sans-vocal]')?.value ?? '') || null },
        }),
      });
      // Le serveur rend la ligne RESOLUE (droits + origines) : on la remet en
      // place telle quelle plutot que de recharger toute la liste, et surtout
      // plutot que de recalculer les droits de notre cote.
      if (reponse?.membre) personnes[i] = { ...personnes[i], ...reponse.membre };
      enEdition = null;
      repeindreUneLigne(id);
      direSurLaLigne(id, 'enregistré');
      peindreLaMatrice();
    } catch (err) {
      // ⚠️ LA RAISON RENDUE PAR LA ROUTE, PAS UN MOT A NOUS. « identifiant
      // invalide » et « roles doit etre une liste » ne se corrigent pas de la
      // meme facon, et « echec » ne se corrige pas du tout.
      direSurLaLigne(id, err.message);
    }
  }

  /**
   * Retirer ses ROLES MAISON — pas son acces.
   *
   * ⚠️ LES DEUX GESTES SONT DISTINCTS, ET LES CONFONDRE COUTE CHER DANS LES
   * DEUX SENS : `DELETE /api/admin/membres/:id` ne touche pas a `allowedUsers`
   * (la personne garde la porte), `DELETE /api/admin/invites/:id/acces` ferme
   * la porte. La question le dit avant, la reponse le confirme apres.
   */
  async function retirerLesRoles(id) {
    const e = personnes.find((x) => x.id === id);
    const quoi = e?.nom || identiteAffichee(e, manquants).titre || id;
    if (!confirm(`Retirer les rôles maison de « ${quoi} » ?\n\n`
      + 'Son accès au lecteur ne bouge pas : il vient des invités nommés et des serveurs. '
      + 'Pour fermer la porte, c\'est « Retirer l\'accès ».')) return;
    direSurLaLigne(id, 'retrait…');
    try {
      await api(`/api/admin/membres/${encodeURIComponent(id)}`, { method: 'DELETE' });
      const i = personnes.findIndex((x) => x.id === id);
      if (i >= 0) personnes[i] = { ...personnes[i], roles: [], nom: '', note: '' };
      enEdition = null;
      repeindreUneLigne(id);
      direSurLaLigne(id, 'rôles retirés — l’accès, lui, n’a pas bougé');
      peindreLaMatrice();
      // Les droits RESOLUS ont change : seul le serveur sait de combien.
      chargerLesPersonnes();
    } catch (err) {
      direSurLaLigne(id, err.message);
    }
  }

  /** LE KICK : fermer la porte. */
  async function retirerLAcces(id) {
    const e = personnes.find((x) => x.id === id);
    const quoi = e?.nom || identiteAffichee(e, manquants).titre || id;
    if (!confirm(`Retirer l'accès de « ${quoi} » ?\n\n`
      + 'Elle sort des invités nommés et ses sessions se ferment. Elle pourra revenir '
      + 'si un serveur autorisé lui ouvre encore la porte.')) return;
    direSurLaLigne(id, 'retrait…');
    try {
      await api(`/api/admin/invites/${encodeURIComponent(id)}/acces`, { method: 'DELETE' });
      personnes = personnes.filter((x) => x.id !== id);
      enEdition = null;
      peindreLesPersonnes();
      peindreLaMatrice();
      direListe('accès retiré');
    } catch (err) {
      direSurLaLigne(id, err.message);
    }
  }

  async function chargerLesPersonnes() {
    // ⚠️ LA ZONE D'ABORD, LE MOT ENSUITE — SINON LE MOT TOMBE DANS LE VIDE.
    // `direListe` ecrit dans `#adm-personnes-etat`, qui n'existe qu'une fois la
    // zone montee. Au TOUT premier chargement, elle ne l'est pas encore : sans
    // cette ligne, « lecture… » et surtout la raison d'un echec s'ecriraient sur
    // un element absent, et `texte()` sortirait en silence.
    assurerLaZonePersonnes();
    direListe('lecture…');
    try {
      const donnees = await api('/api/admin/invites');
      panneListe = null;
      // Une relecture rend l'etat du MOMENT : le refus d'il y a trois minutes,
      // reste colle a sa ligne, se lirait comme la reponse au geste qu'on vient
      // de faire.
      motsDeLigne.clear();
      const tri = separerLesPersonnes({
        invites: donnees.invites ?? [],
        proprietaires: donnees.proprietaires ?? [],
      });
      personnes = tri.personnes;
      chefs = tri.proprietaires;
      roles = donnees.roles ?? roles;
      colonnes = donnees.colonnes ?? colonnes;
      manquants = donnees.profils?.manquants ?? [];
      direListe(resumeDesProfils({
        total: personnes.length,
        resolus: Number(donnees.profils?.resolus ?? 0),
        manquants: manquants.length,
        proprietaires: chefs.length,
      }));
    } catch (err) {
      personnes = [];
      chefs = [];
      manquants = [];
      panneListe = err.message || 'aucune reponse de la route';
      direListe(err.message);
    }
    peindreLesPersonnes();
    peindreLaMatrice();
  }

  // -------------------------------------------------------------------------
  // AJOUTER QUELQU'UN PAR SON IDENTIFIANT DISCORD
  // -------------------------------------------------------------------------
  // ⚠️ LES DEUX CHEMINS COEXISTENT, ET LE SECOND N'EST PAS UN REPLI HONTEUX.
  // La liste du serveur demande un intent privilegie que le bot n'a pas par
  // defaut ; quand elle manque, la route dit la manoeuvre exacte et on l'affiche
  // telle quelle. Le champ d'identifiant, lui, marche toujours : il reste donc
  // visible dans les deux cas, plutot que d'apparaitre seulement en panne.
  function peindreAjout() {
    const zone = $('adm-ajout');
    if (!zone) return;
    const deja = new Set([...personnes, ...chefs].map((m) => m.id));

    const grille = Array.isArray(listeDiscord) && listeDiscord.length
      ? `<div class="adm-serveur-grille">${listeDiscord.map((p) => `<button type="button" class="adm-carte-membre"
            data-poser="${esc(p.id)}"${deja.has(p.id) ? ' disabled' : ''}>
            ${p.avatar ? `<img src="${esc(p.avatar)}" alt="" />` : ''}
            <span class="adm-carte-txt"><strong>${esc(p.pseudo)}</strong>
            <span>${deja.has(p.id) ? 'déjà dans la liste' : esc(p.compte ? `@${p.compte}` : p.id)}</span></span>
          </button>`).join('')}</div>`
      : '';

    const panne = panneDiscord ? `<div class="adm-panne">${esc(panneDiscord)}</div>` : '';
    const vide = !grille && !panne
      ? '<div class="adm-panne">Aucune personne à proposer : le serveur n\'a rendu aucun membre.</div>'
      : '';

    zone.innerHTML = `<div class="inv-panneau">
      <p class="titre-section">Ajouter quelqu’un par son identifiant</p>
      <p class="muted adm-aide adm-aide-serree">
        Dans Discord : Paramètres &gt; Avancé &gt; Mode développeur, puis clic droit
        sur la personne &gt; Copier l’identifiant. Elle rejoint la liste, et on lui
        pose un rôle depuis sa ligne.
      </p>
      ${grille}${panne}${vide}
      <div class="adm-ligne-ajout">
        <input type="text" id="adm-ajout-id" placeholder="Identifiant Discord — 17 à 20 chiffres" />
        <button type="button" id="adm-ajout-poser">Ajouter</button>
        <button type="button" class="ghost" id="adm-ajout-fermer">Fermer</button>
      </div>
      <span class="etat-lang" id="adm-ajout-etat"></span>
    </div>`;

    for (const b of zone.querySelectorAll('[data-poser]')) {
      b.onclick = () => poserMembre(b.dataset.poser);
    }
    surClic('adm-ajout-poser', () => poserMembre($('adm-ajout-id')?.value ?? ''));
    surClic('adm-ajout-fermer', () => { zone.hidden = true; });
  }

  async function poserMembre(idBrut) {
    const id = String(idBrut ?? '').trim();
    const etat = (mot) => texte('adm-ajout-etat', mot);
    // Le meme controle que la route, et pour la meme raison : un identifiant
    // mal recopie doit se dire ICI, ou le champ est encore sous les yeux.
    if (!EST_UN_FLOCON(id)) { etat('Un identifiant Discord fait 17 à 20 chiffres.'); return; }
    if (personnes.some((m) => m.id === id) || chefs.some((m) => m.id === id)) {
      etat('Cette personne est déjà dans la liste.'); return;
    }
    etat('ajout…');
    try {
      const reponse = await api(`/api/admin/membres/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify({ nom: pseudos.get(id)?.pseudo ?? '', roles: [] }),
      });
      const su = pseudos.get(id) ?? null;
      if (reponse?.membre) {
        personnes = [...personnes, {
          ...reponse.membre,
          pseudo: su?.pseudo ?? reponse.membre.pseudo ?? id,
          compte: su?.compte ?? null,
          avatar: su?.avatar ?? null,
          provenances: [{ type: 'membre', detail: 'ajouté à la main' }],
        }];
      }
      const champ = $('adm-ajout-id');
      if (champ) champ.value = '';
      etat('');
      // On ouvre sa fiche dans la foulee : on vient de l'ajouter POUR lui poser
      // un role, et l'y faire cliquer une seconde fois serait une etape de plus
      // pour rien.
      enEdition = id;
      peindreLesPersonnes();
      peindreAjout();
    } catch (err) {
      etat(err.message);
    }
  }

  async function chargerListeDiscord() {
    const guild = guildCourant();
    const adresse = EST_UN_FLOCON(guild)
      ? `/api/admin/membres/discord?guild=${encodeURIComponent(guild)}`
      : '/api/admin/membres/discord';
    try {
      const donnees = await api(adresse);
      listeDiscord = donnees.membres ?? [];
      panneDiscord = null;
      pseudos = new Map(listeDiscord.map((p) => [p.id, p]));
      texte('adm-sous', `${listeDiscord.length} personne${listeDiscord.length > 1 ? 's' : ''} sur le serveur`);
    } catch (err) {
      listeDiscord = null;
      // La raison, mot pour mot : elle porte la manoeuvre (« activez SERVER
      // MEMBERS INTENT… »). La remplacer par « liste indisponible » ferait
      // chercher la panne du cote du serveur Discord.
      panneDiscord = err.message;
      texte('adm-sous', 'la liste du serveur Discord est illisible');
    }
    if (!$('adm-ajout')?.hidden) peindreAjout();
  }

  // -------------------------------------------------------------------------
  // INVITER QUELQU'UN — par lien
  // -------------------------------------------------------------------------
  // ⚠️ LE LIEN N'EST MONTRE QU'UNE FOIS, ET IL FAUT LE DIRE. Il se comporte
  // comme un mot de passe : le serveur n'en garde qu'une empreinte (le motif de
  // `sessions.js`), donc il n'est PAS relisible ensuite. Un ecran qui laisse
  // croire qu'on le retrouvera fait perdre l'invitation — on referme le
  // panneau, et il n'y a plus rien a copier nulle part.
  function peindreInvitations() {
    const zone = $('inv-invitation');
    if (!zone) return;
    const cases = roles.map((r) => `<label class="adm-case">`
      + `<input type="checkbox" data-inv-role="${esc(r.id)}" />${esc(r.nom)}</label>`).join('');

    const fraiche = lienTouNeuf
      ? `<div class="inv-lien">
          <p class="inv-lien-titre">Le lien est prêt — et il ne sera plus jamais affiché.</p>
          <p class="muted adm-aide adm-aide-serree">
            Comme un mot de passe : le serveur n’en garde qu’une empreinte. Copie-le
            maintenant ; en fermant ce panneau, il est perdu (l’invitation, elle,
            reste valable — mais son lien ne se relit pas).
          </p>
          <div class="inv-lien-ligne">
            <input type="text" id="inv-lien-champ" value="${esc(lienTouNeuf)}" readonly />
            <button type="button" id="inv-lien-copier">Copier</button>
          </div>
          <span class="etat-lang" id="inv-lien-etat"></span>
        </div>`
      : '';

    const liste = (() => {
      if (panneInvitations) {
        return `<p class="adm-panne">Les invitations en cours n’ont pas pu être lues :
          ${esc(panneInvitations)}<br />Ce n’est PAS « aucune invitation ».</p>`;
      }
      if (!invitations.length) return '<p class="inv-vide">Aucune invitation en cours.</p>';
      return `<div class="inv-invits">${invitations.map((brut) => {
        const i = etatDeLInvitation(brut);
        const noms = rolesNommes(i.roles, roles);
        return `<article class="inv-invit${i.utilisable ? '' : ' inv-invit-morte'}">
            <span class="inv-invit-roles">${noms.length
              ? noms.map((r) => `<span class="adm-puce"><i class="adm-point"${r.couleur ? ` style="background:${esc(r.couleur)}"` : ''}></i>${esc(r.nom)}</span>`).join('')
              : '<span class="inv-rien">aucun rôle</span>'}</span>
            <span class="inv-invit-etat">${esc(i.mot)}</span>
            <span class="inv-invit-par">${i.par ? `par ${esc(i.par)}` : ''}${i.note ? ` — ${esc(i.note)}` : ''}</span>
            <button type="button" class="ghost" data-annuler-invit="${esc(i.id)}">Annuler</button>
          </article>`;
      }).join('')}</div>`;
    })();

    zone.innerHTML = `<div class="inv-panneau">
      <p class="titre-section">Inviter quelqu’un par lien</p>
      <div class="adm-fiche-grille">
        <div class="adm-champ"><span>Rôles posés à l’entrée</span>
          <div class="adm-role-cases">${cases || '<span class="inv-rien">aucun rôle maison pour l’instant</span>'}</div></div>
        <label class="adm-champ"><span>Combien de personnes au maximum</span>
          <input type="number" id="inv-max" min="1" step="1" value="1" /></label>
        <label class="adm-champ"><span>Combien de jours</span>
          <input type="number" id="inv-jours" min="1" step="1" value="7" /></label>
        <label class="adm-champ"><span>Note</span>
          <input type="text" id="inv-note" placeholder="pour qui, et pourquoi" /></label>
      </div>
      <div class="adm-fiche-pied">
        <button type="button" id="inv-creer">Créer le lien</button>
        <button type="button" class="ghost" id="inv-fermer">Fermer</button>
      </div>
      <span class="etat-lang" id="inv-etat"></span>
      ${fraiche}
      <p class="titre-section inv-invits-titre">Les invitations en cours</p>
      ${liste}
    </div>`;

    surClic('inv-creer', creerLInvitation);
    surClic('inv-fermer', () => {
      // ⚠️ FERMER JETTE LE LIEN, ET C'EST LE COMPORTEMENT HONNETE : le garder
      // en memoire donnerait l'impression qu'on peut toujours le retrouver.
      lienTouNeuf = null;
      zone.hidden = true;
    });
    surClic('inv-lien-copier', copierLeLien);
    for (const b of zone.querySelectorAll('[data-annuler-invit]')) {
      b.onclick = () => annulerLInvitation(b.dataset.annulerInvit);
    }
  }

  async function copierLeLien() {
    const champ = $('inv-lien-champ');
    const etat = (mot) => texte('inv-lien-etat', mot);
    if (!champ) return;
    champ.select?.();
    try {
      await navigator.clipboard.writeText(champ.value);
      etat('copié — colle-le tout de suite quelque part');
    } catch (err) {
      // ⚠️ LE PRESSE-PAPIERS PEUT ETRE REFUSE (page non securisee, permission
      // coupee) : on le DIT, et le champ reste selectionne pour un Ctrl+C.
      etat(`copie refusée par le navigateur (${err.message}) — le lien est sélectionné, fais Ctrl+C`);
    }
  }

  async function creerLInvitation() {
    const zone = $('inv-invitation');
    const etat = (mot) => texte('inv-etat', mot);
    const choisis = [...(zone?.querySelectorAll('[data-inv-role]') ?? [])]
      .filter((c) => c.checked).map((c) => c.dataset.invRole);
    const maximum = Number($('inv-max')?.value ?? 1);
    const jours = Number($('inv-jours')?.value ?? 7);
    if (!Number.isFinite(maximum) || maximum < 1) { etat('« Combien de personnes » vaut au moins 1.'); return; }
    if (!Number.isFinite(jours) || jours < 1) { etat('« Combien de jours » vaut au moins 1.'); return; }
    etat('création…');
    try {
      const r = await api('/api/admin/invitations', {
        method: 'POST',
        body: JSON.stringify({
          roles: choisis, maximum, jours, note: $('inv-note')?.value ?? '',
        }),
      });
      // Le lien peut s'appeler `lien` ou `url` selon la route : les deux sont
      // acceptes, et son ABSENCE se dit plutot que de laisser un champ vide.
      lienTouNeuf = String(r?.lien ?? r?.url ?? '').trim() || null;
      if (r?.invitation) invitations = [r.invitation, ...invitations];
      etat(lienTouNeuf ? '' : 'invitation créée, mais la route n\'a rendu aucun lien — regarde ses journaux.');
      peindreInvitations();
    } catch (err) {
      etat(err.message);
    }
  }

  async function annulerLInvitation(id) {
    const etat = (mot) => texte('inv-etat', mot);
    etat('annulation…');
    try {
      await api(`/api/admin/invitations/${encodeURIComponent(id)}`, { method: 'DELETE' });
      invitations = invitations.filter((x) => String(x?.id ?? '') !== String(id));
      etat('invitation annulée');
      peindreInvitations();
    } catch (err) {
      etat(err.message);
    }
  }

  async function chargerInvitations() {
    try {
      const donnees = await api('/api/admin/invitations');
      invitations = donnees.invitations ?? [];
      panneInvitations = null;
    } catch (err) {
      invitations = [];
      panneInvitations = err.message;
    }
    if (!$('inv-invitation')?.hidden) peindreInvitations();
  }

  // =========================================================================
  // SECTION 2 — LES ROLES ET LEURS PERMISSIONS
  // =========================================================================
  // Toji : « j'ai une section avec la gestion des roles, et je peux directement
  // ajouter un role. Je peux avoir la matrice de role, la matrice de
  // permissions et tout, avec toutes les categories a gauche. Ensuite, c'est
  // une sorte de SPLIT LAYER avec, a gauche, une liste de toutes les
  // categories, et a droite, j'ai directement toutes les permissions. Je peux
  // par exemple valider ou cancel n'importe quelle categorie ou n'importe quel
  // role. Je peux TOUT valider ou TOUT refuser d'un coup. »

  const roleCourant = () => roles.find((r) => r.id === roleOuvert) ?? roles[0] ?? null;

  /** Les pastilles de roles — le choix de celui qu'on regle. */
  function barreDesRoles() {
    const courant = roleCourant();
    if (!roles.length) {
      return '<p class="inv-vide">Aucun rôle maison. Le nom d\'abord, les permissions ensuite — '
        + 'c\'est l\'ordre naturel.</p>';
    }
    return `<div class="adm-roles-barre">${roles.map((r) => {
      const m = barreDePouvoir(r);
      return `<button type="button" class="adm-role-chip${courant && r.id === courant.id ? ' actif' : ''}"
          data-role="${esc(r.id)}" title="${esc(`${compteDeMembres(r.id, personnes)} le porte`)}">
          <i class="adm-point"${r.couleur ? ` style="background:${esc(r.couleur)}"` : ''}></i>
          <span class="adm-role-chip-nom">${esc(r.nom)}</span>
          <span class="adm-role-chip-part">${esc(m.texte)}</span>
        </button>`;
    }).join('')}</div>`;
  }

  /**
   * LA BARRE DE POUVOIR.
   *
   * ⚠️ TOUT CE QUI S'AFFICHE ICI VIENT DE `barreDePouvoir`, ET RIEN N'Y EST
   * RECALCULE — ni le pourcentage (une decimale sous 10 %, un entier au-dessus,
   * c'est deja ce qu'il rend), ni le nombre de permissions, ni le compte des
   * sensibles. Deux calculs divergent, et c'est toujours celui qui s'affiche
   * qui aura tort.
   */
  function blocDePouvoir(role) {
    const m = barreDePouvoir(role);
    const parNiveau = NIVEAUX.map((n) => `<span class="adm-niveau-part" title="${esc(n.description)}">`
      + `${esc(n.nom)} <b>${m.parNiveau[n.niveau] ?? 0}</b></span>`).join('');
    // L'echelle est faite des roles REELLEMENT presents, jamais de chiffres
    // recopies : recopies, ils vieilliraient a la premiere permission cochee.
    const echelle = [...roles]
      .map((r) => ({ nom: r.nom, p: barreDePouvoir(r) }))
      .sort((a, b) => a.p.pourcent - b.p.pourcent)
      .map((x) => `${esc(x.p.texte)} ${esc(x.nom)}`)
      .join(' · ');
    return `<div class="adm-pouvoir">
      <div class="adm-pouvoir-haut">
        <span class="adm-pouvoir-part">${esc(m.texte)}</span>
        <span class="adm-pouvoir-dit">${m.comptees} permission${m.comptees > 1 ? 's' : ''}
          · ${m.poids} points sur ${m.total}${m.sensibles.length
  ? ` · ${m.sensibles.length} sensible${m.sensibles.length > 1 ? 's' : ''}` : ''}</span>
      </div>
      <div class="adm-jauge"><i style="width:${m.largeur}%"></i></div>
      <div class="adm-pouvoir-niveaux">${parNiveau}</div>
      ${echelle ? `<p class="adm-echelle">L’échelle, telle qu’elle est aujourd’hui : ${echelle} · 100 % = tout.</p>` : ''}
    </div>`;
  }

  /** Le role regle : son nom, son sort, son pouvoir, et les deux gestes larges. */
  function blocDuReglage(role) {
    if (!role) return '';
    return `<div class="adm-regle">
      <div class="adm-regle-tete">
        <i class="adm-point adm-point-gros"${role.couleur ? ` style="background:${esc(role.couleur)}"` : ''}></i>
        <input type="text" class="adm-role-nom" id="adm-role-renommer" value="${esc(role.nom)}" />
        <span class="adm-role-membres">${esc(compteDeMembres(role.id, personnes))}</span>
        <button type="button" class="ghost" id="adm-role-supprimer">Supprimer</button>
      </div>
      ${role.note ? `<p class="muted adm-aide adm-aide-serree">${esc(role.note)}</p>` : ''}
      ${blocDePouvoir(role)}
      <div class="adm-gestes">
        <button type="button" id="adm-tout-role">Tout valider pour ce rôle</button>
        <button type="button" class="ghost" id="adm-rien-role">Tout refuser pour ce rôle</button>
        <span class="adm-mot" id="adm-mot-role">${esc(motDe('role'))}</span>
      </div>
    </div>`;
  }

  /** LE VOLET GAUCHE : les huit categories, et « accordees / total » pour chacune. */
  function voletDesCategories(role) {
    return `<div class="adm-cats" id="adm-cats">${CATEGORIES.map((c) => {
      const compte = compteDeLaCategorie(c.cle, role);
      const pleine = compte.accordees === compte.total && compte.total > 0;
      return `<button type="button" class="adm-cat${c.cle === categorieOuverte ? ' actif' : ''}${pleine ? ' pleine' : ''}"
          data-cat="${esc(c.cle)}">
          <span class="adm-cat-icone">${esc(c.icone)}</span>
          <span class="adm-cat-nom">${esc(c.nom)}</span>
          <span class="adm-cat-compte">${compte.accordees} / ${compte.total}</span>
        </button>`;
    }).join('')}</div>`;
  }

  /** LE VOLET DROIT : les permissions de la categorie ouverte. */
  function voletDesPermissions(role) {
    const cat = CATEGORIES.find((c) => c.cle === categorieOuverte) ?? CATEGORIES[0];
    if (!cat) return '<div class="adm-perms" id="adm-perms"></div>';
    const compte = compteDeLaCategorie(cat.cle, role);
    const tenu = new Set(jeuDuRole(role));
    const lignes = permissionsDeLaCategorie(cat.cle).map((p) => `<div class="adm-perm${
      p.sensible ? ' adm-perm-sensible' : ''}" data-perm="${esc(p.cle)}">
        <label class="adm-perm-case">
          <input type="checkbox" data-permission="${esc(p.cle)}"${tenu.has(p.cle) ? ' checked' : ''} />
          <span class="adm-perm-nom">${esc(p.nom)}</span>
        </label>
        <span class="adm-perm-marques">
          ${p.sensible ? '<span class="adm-perm-rouge" title="Seul un propriétaire peut l’accorder — le serveur refuse les autres en 403.">sensible</span>' : ''}
          <span class="adm-perm-niveau" title="${esc(NIVEAUX.find((n) => n.niveau === p.niveau)?.description ?? '')}">${esc(nomDuNiveau(p.niveau))}</span>
        </span>
        <p class="adm-perm-dit">${esc(p.description)}</p>
        <code class="adm-perm-cle">${esc(p.cle)}</code>
        <span class="adm-mot" id="adm-mot-p-${esc(p.cle)}">${esc(motDe(`p-${p.cle}`))}</span>
      </div>`).join('');
    return `<div class="adm-perms" id="adm-perms">
      <div class="adm-perms-tete">
        <span class="adm-perms-titre">${esc(cat.icone)} ${esc(cat.nom)}</span>
        <span class="adm-cat-compte">${compte.accordees} / ${compte.total}</span>
        <div class="adm-gestes">
          <button type="button" id="adm-tout-cat">Tout valider</button>
          <button type="button" class="ghost" id="adm-rien-cat">Tout refuser</button>
        </div>
        <span class="adm-mot" id="adm-mot-cat">${esc(motDe('cat'))}</span>
      </div>
      ${lignes}
    </div>`;
  }

  function peindreLaMatrice() {
    const zone = $('adm-roles');
    if (!zone) return;
    const role = roleCourant();
    if (role) roleOuvert = role.id;
    zone.innerHTML = `${barreDesRoles()}${blocDuReglage(role)}
      ${role ? `<div class="adm-split">${voletDesCategories(role)}${voletDesPermissions(role)}</div>` : ''}`;

    for (const b of zone.querySelectorAll('[data-role]')) {
      b.onclick = () => {
        if (roleOuvert === b.dataset.role) return;
        roleOuvert = b.dataset.role;
        // Les mots d'un autre role n'ont rien a dire sur celui-ci.
        motsMatrice.clear();
        peindreLaMatrice();
      };
    }
    for (const b of zone.querySelectorAll('[data-cat]')) {
      b.onclick = () => {
        categorieOuverte = b.dataset.cat;
        motsMatrice.delete('cat');
        peindreLaMatrice();
      };
    }
    for (const c of zone.querySelectorAll('[data-permission]')) {
      c.onchange = () => basculerUnePermission(c.dataset.permission, c.checked);
    }
    const nom = $('adm-role-renommer');
    if (nom) nom.onchange = () => renommer(roleOuvert, nom.value);
    surClic('adm-role-supprimer', () => supprimer(roleOuvert));
    surClic('adm-tout-role', () => gesteLarge({ tout: true, categorie: null, place: 'role' }));
    surClic('adm-rien-role', () => gesteLarge({ tout: false, categorie: null, place: 'role' }));
    surClic('adm-tout-cat', () => gesteLarge({ tout: true, categorie: categorieOuverte, place: 'cat' }));
    surClic('adm-rien-cat', () => gesteLarge({ tout: false, categorie: categorieOuverte, place: 'cat' }));
  }

  /**
   * Ecrit le jeu COMPLET des permissions du role regle.
   *
   * ⚠️ UN JEU COMPLET, JAMAIS UN DELTA. `majRole` le dit : « absent » doit
   * vouloir dire « decochee », sinon on ne peut plus rien retirer.
   *
   * ⚠️ ET LA CASE REVIENT EN ARRIERE SUR REFUS, EN DISANT POURQUOI. L'ecran
   * coche tout de suite (sinon chaque clic attend un aller-retour), donc il doit
   * savoir defaire : `avant` est garde, et un 403 le remet en place A COTE DU
   * GESTE. Une case qui reste cochee apres un refus est une UI fausse — celle
   * qui fait croire qu'on a donne un droit qu'on n'a pas donne.
   */
  async function ecrireLesPermissions(voulues, { place, mot = '' }) {
    const role = roleCourant();
    if (!role) return;
    const avant = jeuDuRole(role);
    const i = roles.findIndex((r) => r.id === role.id);
    roles[i] = { ...role, permissions: voulues };
    peindreLaMatrice();
    direMatrice(place, mot || 'enregistrement…');
    try {
      const r = await api(`/api/admin/roles-maison/${encodeURIComponent(role.id)}`, {
        method: 'PUT', body: JSON.stringify({ permissions: voulues }),
      });
      if (r?.role) roles[i] = r.role;
      direMatrice(place, mot || 'enregistré');
      peindreLaMatrice();
      // Les droits RESOLUS des gens qui portent ce role viennent de changer, et
      // seul le serveur sait de combien : on relit la seule liste qui existe.
      chargerLesPersonnes();
    } catch (err) {
      roles[i] = { ...role, permissions: avant };
      direMatrice(place, err.message);
      peindreLaMatrice();
    }
  }

  function basculerUnePermission(cle, coche) {
    const role = roleCourant();
    if (!role) return;
    const tenu = new Set(jeuDuRole(role));
    if (coche) tenu.add(cle); else tenu.delete(cle);
    // ⚠️ ON N'EMPECHE PAS LE CLIC SUR UNE SENSIBLE, ET C'EST VOULU : « un ecran
    // qui grise la case ne protege rien, il cache juste le bouton » (la
    // conception). Le serveur refuse en 403 et sa raison s'affiche ICI, sur la
    // ligne de la permission — ce qui apprend la regle au lieu de la cacher.
    ecrireLesPermissions(jeuDuRole([...tenu]), { place: `p-${cle}` });
  }

  /**
   * TOUT VALIDER / TOUT REFUSER — pour la categorie ouverte, ou pour le role.
   *
   * ⚠️ « TOUT VALIDER » N'ACCORDE JAMAIS UNE SENSIBLE A QUI N'EST PAS
   * PROPRIETAIRE, et il le DIT en une ligne. Le serveur refuserait de toute
   * facon (403) — mais un ecran qui envoie une requete vouee au 403 fait passer
   * une regle pour une panne.
   */
  function gesteLarge({ tout, categorie, place }) {
    const role = roleCourant();
    if (!role) return;
    const calcul = tout
      ? toutValider(role, { categorie, proprietaire: jeSuisProprietaire })
      : toutRefuser(role, { categorie, proprietaire: jeSuisProprietaire });
    const mot = motDesSensiblesEcartees(calcul.ecartees, { geste: tout ? 'accorder' : 'retirer' });
    const avecRaison = mot && pourquoiPasProprietaire ? `${mot} (${pourquoiPasProprietaire})` : mot;
    // Rien a ecrire : le geste ne changerait rien, on le dit plutot que de
    // lancer une requete qui rendrait le meme role.
    if (jeuDuRole(calcul.permissions).join() === jeuDuRole(role).join()) {
      direMatrice(place, avecRaison || 'rien à changer — c’était déjà l’état.');
      return;
    }
    ecrireLesPermissions(calcul.permissions, { place, mot: avecRaison });
  }

  async function renommer(id, valeur) {
    const nom = String(valeur ?? '').trim();
    // ⚠️ UN NOM VIDE FERAIT DISPARAITRE LE ROLE (la validation d'ecriture
    // ecarte les roles sans nom). La route refuse deja ; on refuse ici aussi,
    // ou l'on peut remettre l'ancien nom dans le champ.
    if (!nom) {
      direRole('Un rôle doit avoir un nom.');
      peindreLaMatrice();
      return;
    }
    direRole('enregistrement…');
    try {
      const r = await api(`/api/admin/roles-maison/${encodeURIComponent(id)}`, {
        method: 'PUT', body: JSON.stringify({ nom }),
      });
      poserRole(r.role);
      direRole('enregistré');
    } catch (err) {
      direRole(err.message);
      peindreLaMatrice();
    }
  }

  /** Remplace un role dans la liste, et repeint les deux sections. */
  function poserRole(role) {
    if (!role) return;
    const i = roles.findIndex((r) => r.id === role.id);
    if (i >= 0) roles[i] = role; else roles.push(role);
    roleOuvert = role.id;
    peindreLaMatrice();
    // Les personnes portent le NOM du role dans leurs puces et dans leur menu :
    // le renommer sans les repeindre laisserait l'ancien nom a l'ecran.
    peindreLesPersonnes();
  }

  async function supprimer(id) {
    const role = roles.find((r) => r.id === id);
    // ⚠️ LE NOMBRE DE PERSONNES CONCERNEES EST DANS LA QUESTION.
    // Supprimer un role n'est pas un rangement : c'est retirer des droits a des
    // gens. La confirmation le dit avant, la reponse le confirme apres.
    if (!confirm(`Supprimer le rôle « ${role?.nom ?? id} » ?\n\n`
      + `${compteDeMembres(id, personnes)} le porte actuellement et perdra ce qu'il ouvrait.`)) return;
    direRole('suppression…');
    try {
      const bilan = await api(`/api/admin/roles-maison/${encodeURIComponent(id)}`, { method: 'DELETE' });
      roles = roles.filter((r) => r.id !== id);
      if (roleOuvert === id) roleOuvert = roles[0]?.id ?? null;
      direRole(`supprimé — ${bilan?.membresTouches ?? 0} personne(s) touchée(s)`);
      peindreLaMatrice();
      // Le role disparu n'accorde plus rien (regle 4 de `droitsDe`) : les droits
      // resolus de ceux qui le portaient ont change aussi.
      chargerLesPersonnes();
    } catch (err) {
      direRole(err.message);
    }
  }

  /**
   * CREER UN ROLE — ici, et pas sur la ligne d'une personne.
   *
   * Toji : « le nouveau role, il sert a rien [sur la ligne], parce que je veux
   * juste APPLIQUER un role ». On cree le role la ou l'on regle ce qu'il
   * ouvre ; on l'applique la ou l'on voit les gens.
   */
  async function creerRole() {
    const champ = $('adm-role-nom');
    const nom = String(champ?.value ?? '').trim();
    if (!nom) { direRole('Un rôle doit avoir un nom.'); return; }
    direRole('création…');
    try {
      const r = await api('/api/admin/roles-maison', { method: 'POST', body: JSON.stringify({ nom }) });
      if (champ) champ.value = '';
      direRole('créé — coche ses permissions à droite');
      motsMatrice.clear();
      poserRole(r.role);
    } catch (err) {
      direRole(err.message);
    }
  }

  async function chargerRoles() {
    try {
      const donnees = await api('/api/admin/roles-maison');
      roles = donnees.roles ?? [];
      colonnes = donnees.colonnes ?? colonnes;
      direRole('');
    } catch (err) {
      direRole(err.message);
    }
    peindreLaMatrice();
  }

  /**
   * QUI REGARDE — et cette reponse decide de « tout valider ».
   *
   * ⚠️ EN CAS DE PANNE, ON SE CROIT NON-PROPRIETAIRE. C'est le seul defaut sur
   * lequel se tromper ne donne rien a personne : au pire, un proprietaire doit
   * cocher ses quatre sensibles a la main, et l'ecran lui dit pourquoi.
   * L'inverse — se croire proprietaire par defaut — enverrait un « tout
   * valider » portant `service.admin` a chaque ouverture d'ecran.
   */
  async function chargerMoi() {
    try {
      const moi = await api('/api/moi');
      moiId = String(moi?.id ?? '');
      jeSuisProprietaire = moi?.categorie === 'proprietaire';
      pourquoiPasProprietaire = jeSuisProprietaire ? '' : 'tu n’es pas propriétaire de la maison';
    } catch (err) {
      jeSuisProprietaire = false;
      pourquoiPasProprietaire = `on n’a pas pu vérifier qui tu es : ${err.message}`;
    }
  }

  // =========================================================================
  // SECTION 3 — LES POINTS, L'ÉCONOMIE DE SÉANCE
  // =========================================================================
  // Toji (14/09) : « dans la section admin, on mette toute la gestion au niveau
  // des points, un peu comme ce qu'on a fait pour Roma, mais en simplifié […]
  // on peut soit choisir pour un épisode, soit choisir une durée […] on peut
  // payer tant et on fait des discounts pour des grandes sessions […] s'il y a
  // un rush, les gens peuvent l'économiser. »
  //
  // ⚠️ CETTE ZONE EST ÉCRITE DANS `public/index.html`, PAS MONTÉE ICI. Elle est
  // entièrement statique — ses champs ne dépendent d'aucune donnée — et la
  // monter en JS ferait vivre ses identifiants à DEUX endroits le temps d'un
  // repeint. Seules les LIGNES (paliers, multiplicateurs) et l'aperçu se
  // peignent ; les lignes ne portent donc jamais d'identifiant, seulement
  // `data-i` et des classes.
  //
  // ⚠️ ET AUCUN CALCUL NE SE FAIT ICI. Le prix d'un lot, la remise de volume, la
  // part de chacun, les bornes de chaque réglage : tout vient de `points`
  // (`src/points.js`), qui importe les fichiers que le bot exécute. Un écran
  // qui referait la division afficherait un prix que le bot ne demande pas —
  // et ce désaccord-là ne lève jamais, il se découvre un soir, sur un refus.
  // =========================================================================

  /** Le groupe de champs auquel appartient chaque prix — pour n'afficher que le mode choisi. */
  const GROUPE_DU_CHAMP = {
    prixEpisode: 'adm-pts-champs-episode',
    prixFilm: 'adm-pts-champs-episode',
    prixTranche: 'adm-pts-champs-duree',
    minutesParTranche: 'adm-pts-champs-duree',
  };

  /** Les champs fixes de la zone : leur identifiant, et la clé de réglage qu'ils portent. */
  const CHAMPS_POINTS = [
    ['adm-pts-minute', 'parMinuteVocale'],
    ['adm-pts-message', 'parMessage'],
    ['adm-pts-delai', 'delaiEntreMessagesS'],
    ['adm-pts-minimum', 'minimumEnVocal'],
    ['adm-pts-plafond', 'plafond'],
    ['adm-pts-booster', 'remiseBoosterPourcent'],
    ['adm-pts-prix-episode', 'prixEpisode'],
    ['adm-pts-prix-film', 'prixFilm'],
    ['adm-pts-prix-tranche', 'prixTranche'],
    ['adm-pts-minutes-tranche', 'minutesParTranche'],
    // ⚠️ LES SIX REGLAGES CI-DESSOUS EXISTENT ET SONT BORNES DEPUIS LE DEBUT
    // (`apps/bot/src/reglages-points.js` : DEFAUTS et BORNES), et ils etaient
    // atteignables UNIQUEMENT en editant `access.json` a la main sur le
    // serveur : ni cette table ni le balisage ne les portaient. Le plafond de
    // 120 points par jour — celui qui empeche un micro laisse ouvert toute la
    // nuit de rafler la mise — ne se reglait donc pas depuis l'ecran.
    ['adm-pts-max-jour-vocal', 'maxParJourVocal'],
    ['adm-pts-max-jour-messages', 'maxParJourMessages'],
    // La reclamation quotidienne. Son interrupteur (`dailyActif`) ne passe PAS
    // par ici : c'est une case a cocher, pas un champ de valeur — voir
    // `lireLesChampsPoints` et `peindreLesPoints`.
    ['adm-pts-daily-base', 'dailyBase'],
    ['adm-pts-daily-par-jour', 'dailyParJour'],
    ['adm-pts-daily-max-bonus', 'dailyMaxBonus'],
    ['adm-pts-daily-max-jours', 'dailyMaxJours'],
    // Les groupes (phase 4, `economie.js` : prixDuGroupe). Propriétaire seul :
    // le serveur garde ces clés telles quelles pour tout autre gestionnaire.
    ['adm-pts-plafond-groupes', 'plafondGroupes'],
    ['adm-pts-charge-max', 'chargeMaxPourcent'],
    ['adm-pts-coef-leger', 'coefLegerPourcent'],
    ['adm-pts-coef-haut', 'coefHautPourcent'],
    ['adm-pts-creuses-debut', 'creusesDebut'],
    ['adm-pts-creuses-fin', 'creusesFin'],
    ['adm-pts-coef-creuses', 'coefCreusesPourcent'],
    ['adm-pts-pic-debut', 'picDebut'],
    ['adm-pts-pic-fin', 'picFin'],
    ['adm-pts-coef-pic', 'coefPicPourcent'],
    ['adm-pts-bonus-groupe', 'bonusGroupe'],
    ['adm-pts-bonus-groupe-jour', 'bonusGroupeMaxJour'],
    ['adm-pts-remise-serie', 'remiseSeriePourcent'],
    ['adm-pts-remise-suivant', 'remiseSuivantPourcent'],
    ['adm-pts-remise-cache', 'remiseCachePourcent'],
    ['adm-pts-plancher', 'plancherPourcent'],
    ['adm-pts-confirmer-des', 'confirmerDes'],
    ['adm-pts-anciennete-membre', 'ancienneteMembreJours'],
    ['adm-pts-anciennete-compte', 'ancienneteCompteJours'],
  ];

  // Fidélité et promotions en texte : « 10:5, 50:10 » et une promo par ligne
  // « série ; remise ; AAAA-MM-JJ ; titre ». `lirePoints` borne et trie derrière.
  const fideliteEnTexte = (l = []) => l.map((p) => `${p.vus}:${p.remisePourcent}`).join(', ');
  const fideliteDuTexte = (t) => String(t).split(',').map((x) => x.split(':').map((n) => n.trim()))
    .filter(([v, r]) => v && r).map(([vus, remisePourcent]) => ({ vus: Number(vus), remisePourcent: Number(remisePourcent) }));
  const promosEnTexte = (l = []) => l.map((p) => [p.serie, p.remisePourcent, p.fin, p.titre ?? ''].join(' ; ')).join('\n');
  const promosDuTexte = (t) => String(t).split('\n').map((x) => x.split(';').map((n) => n.trim()))
    .filter(([serie]) => serie).map(([serie, remisePourcent, fin, titre]) => ({ serie, remisePourcent: Number(remisePourcent), fin, titre: titre || null }));

  /**
   * Le brouillon rattrape ce qui est dans les champs.
   *
   * ⚠️ ON GARDE LA CHAÎNE, ON NE CONVERTIT PAS. Un champ vidé vaut `''` dans le
   * brouillon ; c'est `lirePoints` qui décidera ce que cela veut dire, au
   * moment de l'aperçu et de l'enregistrement — et il le décidera de la même
   * façon que le bot, ce qui est tout l'intérêt.
   */
  function lireLesChampsPoints() {
    if (!brouillonPoints) return;
    const suite = { ...brouillonPoints };
    // ⚠️ UN CHAMP QUE LA PAGE NE PORTE PAS NE DOIT RIEN ECRASER.
    //
    // L'ancienne forme lisait `$(id)?.value ?? ''` : un champ absent du
    // balisage rendait `''`, et `''` n'est PAS « on n'y touche pas ». Il
    // traverse `lirePoints` -> `entier('')` -> `Math.round(Number(''))` === 0,
    // qui est fini, donc retenu : le reglage repartait A ZERO a chaque
    // enregistrement. Tant que les champs de la daily et des plafonds du jour
    // ne sont pas dans `public/index.html`, les inscrire dans `CHAMPS_POINTS`
    // avec l'ancienne lecture aurait remis `dailyBase`, `dailyParJour`,
    // `dailyMaxBonus`, `dailyMaxJours` et les deux `maxParJour*` a 0 des le
    // premier « Enregistrer » — sans que rien ne leve.
    //
    // Un reglage qu'on n'a pas sous les yeux se GARDE tel quel.
    const lire = (id, cle) => { const el = $(id); if (el) suite[cle] = el.value; };
    const cocher = (id, cle) => { const el = $(id); if (el) suite[cle] = !!el.checked; };
    cocher('adm-pts-actif', 'actif');
    // ⚠️ LA DAILY A SON PROPRE INTERRUPTEUR, ET CE N'EST PAS `actif`.
    // `actif: false` couperait TOUTE l'economie (les seances redeviennent
    // libres) ; un serveur peut vouloir garder les points du vocal sans la
    // reclamation quotidienne. Et cote moteur, seul un `true` explicite la
    // rallume (`reglages-points.js` : `out.dailyActif = src.dailyActif === true`)
    // — sans cette case, elle etait INRALLUMABLE depuis le web.
    cocher('adm-pts-daily-actif', 'dailyActif');
    cocher('adm-pts-groupes-actif', 'groupesActif');
    if ($('adm-pts-fidelite')) suite.fidelite = fideliteDuTexte($('adm-pts-fidelite').value);
    if ($('adm-pts-promos')) suite.promos = promosDuTexte($('adm-pts-promos').value);
    lire('adm-pts-mode', 'mode');
    if (!suite.mode) suite.mode = brouillonPoints.mode;
    for (const [id, cle] of CHAMPS_POINTS) lire(id, cle);
    brouillonPoints = suite;
  }

  /** N'afficher que les champs de la façon de facturer retenue. */
  function appliquerLeModePoints() {
    if (!points?.champsDuMode) return;
    const { montres, caches } = points.champsDuMode(brouillonPoints?.mode);
    // Cacher d'abord, montrer ensuite : les deux jeux ne se recouvrent pas, mais
    // l'ordre inverse ferait clignoter le groupe commun le jour où ils le feront.
    for (const champ of caches) { const g = $(GROUPE_DU_CHAMP[champ]); if (g) g.hidden = true; }
    for (const champ of montres) { const g = $(GROUPE_DU_CHAMP[champ]); if (g) g.hidden = false; }
  }

  /** Les options d'un sélecteur de rôle Discord, le rôle déjà posé compris. */
  function optionsDeRole(choisi) {
    const id = String(choisi ?? '');
    const connus = rolesPoints.map((r) => String(r.id));
    // ⚠️ UN RÔLE ENREGISTRÉ QUE DISCORD N'A PAS RENDU RESTE DANS LA LISTE.
    // Sans cette ligne, un hoquet de Discord — ou un rôle supprimé du serveur —
    // ferait retomber le sélecteur sur « aucun » et le prochain enregistrement
    // EFFACERAIT le multiplicateur, sans que personne ne l'ait demandé.
    const orphelin = id && !connus.includes(id) ? [{ id, nom: `${id} — ce rôle n’est plus sur le serveur` }] : [];
    return [{ id: '', nom: '— choisir un rôle —' }, ...rolesPoints, ...orphelin]
      .map((r) => `<option value="${esc(r.id)}"${String(r.id) === id ? ' selected' : ''}>${esc(r.nom)}</option>`)
      .join('');
  }

  /** Les lignes de la grille de volume. */
  function peindreLesPaliers() {
    const zone = $('adm-pts-paliers');
    if (!zone) return;
    const liste = brouillonPoints?.paliers ?? [];
    const unite = points?.champsDuMode?.(brouillonPoints?.mode)?.unite ?? 'épisode';
    if (!liste.length) {
      zone.innerHTML = '<p class="adm-pts-vide">Aucun palier : tout se facture au prix plein, et épargner ne rapporte rien.</p>';
      return;
    }
    zone.innerHTML = liste.map((p, i) => `<div class="adm-pts-ligne" data-i="${i}">
        <span class="adm-pts-mot">À partir de</span>
        <input class="adm-pts-num" type="number" min="1" step="1" data-champ="quantite" value="${esc(p.quantite)}" />
        <span class="adm-pts-mot">${esc(unite)}s d’un coup → remise de</span>
        <input class="adm-pts-num" type="number" min="1" max="100" step="1" data-champ="remisePourcent" value="${esc(p.remisePourcent)}" />
        <span class="adm-pts-mot">%</span>
        <button type="button" class="ghost adm-pts-retirer">Retirer</button>
      </div>`).join('');
    brancherLesLignes(zone, 'paliers');
  }

  /** Les lignes des multiplicateurs par rôle. */
  function peindreLesMultiplicateurs() {
    const zone = $('adm-pts-multis');
    if (!zone) return;
    const liste = brouillonPoints?.multiplicateurs ?? [];
    if (!liste.length) {
      zone.innerHTML = '<p class="adm-pts-vide">Aucun multiplicateur : tout le monde gagne au même rythme.</p>';
      return;
    }
    zone.innerHTML = liste.map((m, i) => `<div class="adm-pts-ligne" data-i="${i}">
        <select data-champ="role">${optionsDeRole(m.role)}</select>
        <span class="adm-pts-mot">gagne ×</span>
        <input class="adm-pts-num" type="number" min="0" max="10" step="0.1" data-champ="facteur" value="${esc(m.facteur)}" />
        <button type="button" class="ghost adm-pts-retirer">Retirer</button>
      </div>`).join('');
    brancherLesLignes(zone, 'multiplicateurs');
  }

  /**
   * Brancher une grille de lignes.
   *
   * ⚠️ ON NE REPEINT PAS LA GRILLE À CHAQUE FRAPPE. Remplacer l'`innerHTML`
   * détruit le champ qu'on est en train de remplir : le curseur saute, et l'on
   * tape un chiffre par clic. Seul l'aperçu se refait ; les lignes ne se
   * repeignent qu'à l'ajout, au retrait et au rechargement.
   */
  function brancherLesLignes(zone, quoi) {
    for (const champ of zone.querySelectorAll('[data-champ]')) {
      champ.oninput = () => {
        const ligne = champ.closest('.adm-pts-ligne');
        brouillonPoints[quoi] = points.changerLigne(
          brouillonPoints[quoi], ligne?.dataset?.i, champ.dataset.champ, champ.value,
        );
        peindreLApercuPoints();
      };
      // Un `<select>` n'émet pas toujours `input` sur les vieux moteurs ; il
      // émet toujours `change`, et le double branchement ne coûte rien.
      champ.onchange = champ.oninput;
    }
    for (const bouton of zone.querySelectorAll('.adm-pts-retirer')) {
      bouton.onclick = () => {
        const ligne = bouton.closest('.adm-pts-ligne');
        brouillonPoints[quoi] = points.retirerLigne(brouillonPoints[quoi], ligne?.dataset?.i);
        peindreLesLignesPoints();
        peindreLApercuPoints();
        direPoints('ligne retirée — pense à enregistrer');
      };
    }
  }

  function peindreLesLignesPoints() {
    peindreLesPaliers();
    peindreLesMultiplicateurs();
  }

  /**
   * L'APERÇU — ce qui rend un barème compréhensible sans l'essayer sur ses invités.
   *
   * « 30 minutes de vocal = 1 épisode », « à 3 personnes : 10 points chacune »,
   * « 5 épisodes d'un coup : 120 points au lieu de 150 ». Ces phrases sont
   * calculées par `apercuDuBareme` avec le moteur de prix du bot, jamais
   * réécrites ici : un aperçu qui ferait son propre calcul finirait par annoncer
   * autre chose que ce qui sera débité.
   */
  function peindreLApercuPoints() {
    const zone = $('adm-pts-apercu');
    if (!zone || !brouillonPoints || !points?.apercuDuBareme) return;
    const lignes = points.apercuDuBareme(brouillonPoints, { roles: rolesPoints });
    zone.innerHTML = `<p class="titre-section">Ce que ce barème donne</p>${
      lignes.map((l) => `<div class="adm-pts-dit">${esc(l.dit)}</div>`).join('')}`;
  }

  function peindreLesPoints() {
    if (!brouillonPoints) return;
    const inter = $('adm-pts-actif');
    if (inter) inter.checked = !!brouillonPoints.actif;
    // La daily porte son propre interrupteur : sans ce repeint, la case
    // resterait decochee apres un chargement alors que le fichier dit `true`,
    // et le premier « Enregistrer » l'eteindrait pour de bon.
    const interDaily = $('adm-pts-daily-actif');
    if (interDaily) interDaily.checked = !!brouillonPoints.dailyActif;
    const interGroupes = $('adm-pts-groupes-actif');
    if (interGroupes) interGroupes.checked = !!brouillonPoints.groupesActif;
    if ($('adm-pts-fidelite')) $('adm-pts-fidelite').value = fideliteEnTexte(brouillonPoints.fidelite);
    if ($('adm-pts-promos')) $('adm-pts-promos').value = promosEnTexte(brouillonPoints.promos);
    const mode = $('adm-pts-mode');
    if (mode) mode.value = brouillonPoints.mode;
    for (const [id, cle] of CHAMPS_POINTS) {
      const champ = $(id);
      if (champ) champ.value = brouillonPoints[cle];
    }
    appliquerLeModePoints();
    peindreLesLignesPoints();
    peindreLApercuPoints();
  }

  async function chargerPoints() {
    // Un bundle qui n'aurait pas reçu le module ne doit pas emporter le reste de
    // l'écran : on le DIT une fois, et les deux autres sections vivent leur vie.
    if (!points?.brouillonDe) {
      tracer?.('admin', 'la logique des points n a pas ete fournie au montage');
      return;
    }
    try {
      const d = await api('/api/admin/points');
      // ⚠️ `roles` EST UNE LISTE, PAS UNE TABLE. La route rend
      // `{ reglages, roles: [{ id, nom, couleur }] }` : la lire autrement
      // laisserait un sélecteur vide sans la moindre erreur — c'est la panne
      // qu'on a déjà payée ailleurs dans ce projet.
      rolesPoints = Array.isArray(d?.roles) ? d.roles : [];
      brouillonPoints = points.brouillonDe(d?.reglages);
      // Les groupes sont au propriétaire seul : le serveur ignorerait ces champs pour un autre.
      if ($('adm-pts-groupes')) $('adm-pts-groupes').hidden = d?.proprietaire === false;
      direPoints(d?.rolesLisibles === false
        ? 'réglages chargés — mais Discord n’a pas rendu la liste des rôles : les multiplicateurs déjà posés sont conservés'
        : '');
    } catch (err) {
      // On garde ce qu'on avait ; à défaut, les défauts — un écran vide ne se
      // règle pas, et il ne dit pas non plus ce qui tourne aujourd'hui.
      brouillonPoints = brouillonPoints ?? points.brouillonDe(null);
      direPoints(err.message);
    }
    peindreLesPoints();
  }

  async function enregistrerPoints() {
    if (!brouillonPoints || !points?.reglagesDuBrouillon) return;
    lireLesChampsPoints();
    // ⚠️ ON NORMALISE AVANT D'ENVOYER, ET LE SERVEUR RENORMALISE DERRIÈRE.
    // Le serveur ne fait confiance à aucun corps de requête — c'est sa part.
    // Mais s'il était le seul à corriger, l'écran continuerait d'afficher ce
    // qu'on a tapé alors que le fichier porte autre chose, jusqu'au prochain
    // rechargement. En passant par la même porte, ce qu'on voit est ce qui part.
    const reglages = points.reglagesDuBrouillon(brouillonPoints);
    direPoints('enregistrement…');
    try {
      const r = await api('/api/admin/points', { method: 'PUT', body: JSON.stringify(reglages) });
      brouillonPoints = points.brouillonDe(r?.reglages);
      peindreLesPoints();
      direPoints('enregistré — le bot l’applique dès le prochain geste, sans redémarrage');
    } catch (err) {
      direPoints(err.message);
    }
  }

  function brancherLesPoints() {
    if (!points?.ajouterPalier) return;
    surClic('adm-pts-relire', chargerPoints);
    surClic('adm-pts-enregistrer', enregistrerPoints);
    // ⚠️ `brancher()` PASSE AVANT `chargerPoints()` — un clic pendant le
    // chargement trouverait le brouillon à `null`. On ne laisse pas passer une
    // exception silencieuse : on dit ce qui se passe et on ne fait rien.
    const surLeBrouillon = (geste) => () => {
      if (!brouillonPoints) { direPoints('le barème n’est pas encore chargé…'); return; }
      lireLesChampsPoints();
      geste();
      peindreLesLignesPoints();
      peindreLApercuPoints();
    };
    surClic('adm-pts-palier-ajouter', surLeBrouillon(() => {
      brouillonPoints.paliers = points.ajouterPalier(brouillonPoints.paliers);
    }));
    surClic('adm-pts-multi-ajouter', surLeBrouillon(() => {
      brouillonPoints.multiplicateurs = points.ajouterMultiplicateur(brouillonPoints.multiplicateurs);
    }));

    const suivre = (id, aussiLeMode = false) => {
      const champ = $(id);
      if (!champ) return;
      champ.oninput = () => {
        lireLesChampsPoints();
        if (aussiLeMode) { appliquerLeModePoints(); peindreLesLignesPoints(); }
        peindreLApercuPoints();
      };
      champ.onchange = champ.oninput;
    };
    // Le mode et l'interrupteur changent ce que la zone MONTRE, pas seulement
    // ce qu'elle calcule : ils repeignent les lignes avec eux (le mot « épisode »
    // devient « tranche » dans la grille de volume).
    suivre('adm-pts-mode', true);
    suivre('adm-pts-actif', true);
    // La case de la daily doit etre suivie elle aussi : sans cela, la cocher ne
    // toucherait au brouillon qu'au moment d'« Enregistrer », et l'apercu
    // resterait sur l'etat d'avant. `suivre` sort de lui-meme quand l'element
    // n'est pas dans la page.
    suivre('adm-pts-daily-actif');
    suivre('adm-pts-groupes-actif');
    for (const [id] of CHAMPS_POINTS) suivre(id);
    surClic('adm-pts-charge-relire', chargerLaCharge);
    // ⚠️ PAS DE CHARGEMENT ICI : le montage passe AVANT la porte, et la charge
    // traverserait le réseau porte fermée. Le premier chargement est dans
    // `peupler()` (porte ouverte) ; on relit ensuite à chaque entrée dans « Points ».
    document.querySelector('#adm-nav .adm-nav-item[data-adm="points"]')?.addEventListener('click', () => { chargerLaCharge(); });
  }

  /** La charge en direct : groupes, flux, file, débit estimé, parts. */
  async function chargerLaCharge() {
    const zone = $('adm-pts-charge');
    if (!zone) return;
    try {
      const c = await api('/api/admin/charge');
      const p = c?.points;
      if (!p) { zone.textContent = 'Charge indisponible.'; return; }
      // Estimé par la qualité des groupes (12/6/4 Mbit/s) ; le relais mesure le sien à côté.
      const debit = Object.values(p.mbitsParQualite ?? {}).reduce((t, n) => t + n, 0);
      const lignes = [
        `${p.ouverts} groupe${p.ouverts > 1 ? 's' : ''} sur ${p.plafond} · ${p.file} en file d’attente${p.actif ? '' : ' · caisse coupée'}`,
        `${c.relaisAmontActifs ?? 0} flux ouverts · ${c.spectateurs ?? 0} spectateurs · ~${debit} Mbit/s estimés (relais : ${c.mbitsEstimes ?? 0})`,
        `En groupe : ${p.parts?.enGroupePourcent ?? 0} % · heures creuses : ${p.parts?.creusesPourcent ?? 0} % · 1080p : ${p.parts?.legerePourcent ?? 0} %`,
        ...(p.groupes ?? []).map((g) => `${g.cible} — ${g.membres} pers. · ${g.qualite}${g.finDansMin !== null && g.finDansMin !== undefined ? ` · fin ~${g.finDansMin} min` : ''}`),
      ];
      zone.innerHTML = lignes.map((l) => `<span class="adm-pts-dit">${esc(l)}</span>`).join('');
    } catch (err) { zone.textContent = `Charge indisponible : ${err.message}`; }
  }

  // =========================================================================
  // LE RAIL — NEUF SECTIONS, UNE SEULE A L'AFFICHE
  // =========================================================================
  // Toji (16/09) : « trié par catégorie, bien référencé, et que ce soit vraiment
  // organisé pour que ça soit facile en termes d'utilisation ».
  //
  // ⚠️ ON MASQUE, ON NE DEMONTE PAS. Chaque section porte du balisage que
  // `main.js` a deja branche par `getElementById` au demarrage de la page :
  // retirer une section du document couperait ses branchements, et la remettre
  // les dedoublerait. `hidden` laisse tout en place — c'est la seule facon de
  // changer de section sans toucher a dix-huit mille lignes de cablage.
  //
  // La section courante est retenue d'une ouverture a l'autre : on revient
  // presque toujours la ou l'on etait.
  let sectionCourante = 'apercu';

  /** Toutes les sections du tableau de bord, dans l'ordre du rail. */
  const lesSections = () => [...document.querySelectorAll('#adm-corps .adm-section')];

  function allerA(cle) {
    const sections = lesSections();
    if (!sections.length) return;
    const voulue = sections.find((x) => x.dataset.adm === cle) ?? sections[0];
    sectionCourante = voulue.dataset.adm;
    for (const x of sections) x.hidden = x !== voulue;
    for (const b of document.querySelectorAll('#adm-nav .adm-nav-item')) {
      const actif = b.dataset.adm === sectionCourante;
      b.classList.toggle('actif', actif);
      if (actif) b.setAttribute('aria-current', 'page');
      else b.removeAttribute('aria-current');
    }
    // Le titre et le chapeau vivent SUR la section, pas dans une table a part :
    // ajouter une section sans penser a sa ligne de table donnerait un ecran
    // intitule « undefined », et c'est arrive assez souvent ailleurs.
    const t = $('adm-titre');
    const c = $('adm-chapeau');
    if (t) t.textContent = voulue.dataset.titre ?? '';
    if (c) c.textContent = voulue.dataset.chapeau ?? '';
    // On remonte : garder le defilement d'une section longue en arrivant sur la
    // suivante fait atterrir au milieu de nulle part.
    const corps = $('adm-corps');
    if (corps) corps.scrollTop = 0;
  }

  function brancherLeRail() {
    for (const b of document.querySelectorAll('#adm-nav .adm-nav-item')) {
      b.onclick = () => allerA(b.dataset.adm);
    }
    // Les cartes et les raccourcis de la vue d'ensemble menent aux sections.
    for (const b of document.querySelectorAll('#v-admin [data-va]')) {
      b.onclick = () => allerA(b.dataset.va);
    }
  }

  // =========================================================================
  // LA PORTE — LE MOT DE PASSE D'ADMINISTRATION
  // =========================================================================
  // Toji (16/09) : « on a la connexion de base via l'utilisateur Discord. Après,
  // dans la section admin, on a un mot de passe admin en plus. Après, dans tous
  // les cas, on a des accès par utilisateur. »
  //
  // Trois couches, et chacune repond a une question differente :
  //   Discord    -> QUI es-tu                          (`auth`)
  //   le mot     -> es-tu en train d'administrer MAINTENANT (`gestionOnly`)
  //   les droits -> qu'as-tu le droit de faire         (`exigeDroit`, la matrice)
  //
  // ⚠️ RIEN N'EST DEMANDE AU SERVEUR TANT QUE LA PORTE EST FERMEE.
  // Peindre le tableau de bord puis poser un voile par-dessus, ce serait charger
  // toutes les donnees d'administration et se contenter de ne pas les montrer.
  let porte = { exigee: false, ouverte: false, jusqua: null };

  async function lirePorte() {
    try {
      porte = await api('/api/admin/porte');
    } catch (err) {
      // Une porte qu'on n'arrive pas a interroger est FERMEE. Le defaut inverse
      // — « on n'a pas pu verifier, donc on entre » — est celui qui se paye.
      porte = { exigee: true, ouverte: false, jusqua: null, panne: err?.message ?? 'illisible' };
    }
    return porte;
  }

  /** Ce qu'il reste avant que la porte se referme, en clair. */
  function motDuReste() {
    if (!porte.exigee) return "Aucun mot de passe d'administration n'est posé.";
    const reste = Number(porte.jusqua) - Date.now();
    if (!Number.isFinite(reste) || reste <= 0) return '';
    // ⚠️ ON ARRONDIT LE TOTAL, PUIS ON COUPE — JAMAIS L'INVERSE.
    //
    // `Math.round((reste % 3600000) / 60000)` rend 60 des que le reste depasse
    // 59 min 30 s, et l'heure, elle, a deja ete prise par `Math.floor`. La
    // capture du parcours l'a montre noir sur blanc : « Porte ouverte encore
    // 7 h 60 ». Ce n'est pas grave et c'est tres visible — le genre de detail
    // qui fait douter du reste de la page.
    const minutes = Math.round(reste / 60000);
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0
      ? `Porte ouverte encore ${h} h ${String(m).padStart(2, '0')}`
      : `Porte ouverte encore ${m} min`;
  }

  function peindreLaPorte() {
    const dehors = $('adm-porte');
    const dedans = $('adm-shell');
    if (!dehors || !dedans) return;
    const ouverte = !porte.exigee || porte.ouverte;
    dehors.hidden = ouverte;
    dedans.hidden = !ouverte;
    texte('adm-porte-reste', ouverte ? motDuReste() : '');
    const sortir = $('adm-sortir');
    // Sans mot de passe pose, « fermer la porte » ne fermerait rien : le bouton
    // disparait plutot que de promettre un geste qui n'existe pas.
    if (sortir) sortir.hidden = !porte.exigee;
    const bilan = $('adm-porte-bilan');
    if (bilan) {
      bilan.textContent = porte.exigee
        ? (motDuReste() || 'La porte est fermée.')
        : "Aucun mot de passe d'administration n'est posé : pose ADMIN_MOT_DE_PASSE "
          + "(8 caractères au moins) dans l'environnement du lecteur pour que cette page en demande un.";
    }
    if (!ouverte) setTimeout(() => $('adm-porte-mot')?.focus(), 30);
  }

  function brancherLaPorte() {
    const form = $('adm-porte-form');
    if (form) {
      form.onsubmit = async (e) => {
        e.preventDefault();
        const champ = $('adm-porte-mot');
        const bouton = $('adm-porte-entrer');
        texte('adm-porte-refus', '');
        if (bouton) bouton.disabled = true;
        try {
          const r = await api('/api/admin/porte', {
            method: 'POST', body: JSON.stringify({ mot: champ?.value ?? '' }),
          });
          porte = { ...porte, ...r };
          if (champ) champ.value = '';   // il ne reste pas dans le DOM apres coup
          peindreLaPorte();
          await peupler();
        } catch (err) {
          // Le serveur ne dit pas POURQUOI le mot est refuse (voir la route) :
          // on ne l'invente pas ici non plus.
          texte('adm-porte-refus', err?.message || 'Mot de passe refusé.');
          $('adm-porte-mot')?.select?.();
        } finally {
          if (bouton) bouton.disabled = false;
        }
      };
    }
    surClic('adm-porte-retour', fermer);
    surClic('adm-sortir', async () => {
      try {
        porte = { ...porte, ...(await api('/api/admin/porte', { method: 'DELETE' })) };
      } catch (err) {
        // ⚠️ AUCUN CATCH MUET — regle du depot, et elle a du sens ICI EN
        // PARTICULIER. « Fermer la porte » qui echoue cote serveur laisse la
        // session OUVERTE : on referme l'ecran quand meme (c'est le geste le
        // plus sur du point de vue de qui clique), mais on le DIT, sinon on
        // croit etre sorti alors que la session peut encore administrer.
        tracer?.('admin', `fermeture de la porte refusee (${err?.message ?? err}) `
          + '— l\'ecran se referme, mais la session peut rester ouverte jusqu\'a son echeance');
        porte = { ...porte, ouverte: false, jusqua: null };
      }
      peindreLaPorte();
    });
    surClic('adm-porte-relire', async () => { await lirePorte(); peindreLaPorte(); });
  }

  // =========================================================================
  // LA VUE D'ENSEMBLE — QUATRE CHIFFRES, TOUS VENUS D'UNE ROUTE
  // =========================================================================
  // ⚠️ AUCUN CHIFFRE N'EST DEDUIT NI ESTIME. Une carte qui n'a pas sa reponse
  // affiche « — » et dit pourquoi : un tableau de bord qu'on croit sur parole
  // est le seul genre de tableau de bord qui fasse des degats.
  function carte(cle, chiffre, note) {
    const c = $(`adm-c-${cle}`);
    const n = $(`adm-c-${cle}-note`);
    if (c) c.textContent = chiffre;
    if (n) n.textContent = note;
  }

  async function chargerLApercu() {
    const cartes = [
      ['personnes', '/api/admin/access', (r) => {
        const invites = Object.keys(r?.allowedUsers ?? {}).length;
        const chefs = (r?.proprietaires ?? []).length;
        return [String(invites), chefs ? `${chefs} propriétaire(s) en plus` : 'invités enregistrés'];
      }],
      ['demandes', '/api/admin/demandes', (r) => {
        const n = (r?.demandes ?? []).length;
        return [String(n), n ? "en attente d'une décision" : "rien n'attend"];
      }],
      ['sources', '/api/admin/access', (r) => {
        const src = r?.sources ?? [];
        const actives = src.filter((x) => x?.actif !== false).length;
        return [String(actives), `sur ${src.length} branchée(s)`];
      }],
      ['seances', '/api/admin/seances', (r) => {
        const n = Object.keys(r?.seances ?? {}).length;
        return [String(n), n ? 'en cours' : 'aucune en cours'];
      }],
    ];
    await Promise.all(cartes.map(async ([cle, route, lire]) => {
      try {
        const [chiffre, note] = lire(await api(route));
        carte(cle, chiffre, note);
      } catch (err) {
        carte(cle, '—', err?.message || 'illisible');
      }
    }));
  }

  // -------------------------------------------------------------------------
  // OUVRIR, FERMER
  // -------------------------------------------------------------------------
  function brancher() {
    surClic('adm-retour', fermer);
    surClic('adm-role-creer', creerRole);
    const nom = $('adm-role-nom');
    if (nom) nom.onkeydown = (e) => { if (e.key === 'Enter') creerRole(); };
    brancherLesPoints();
    brancherLeRail();
    brancherLaPorte();
  }

  async function ouvrir() {
    if (!$('v-admin')) {
      // Un balisage sans cet ecran ne doit pas emporter le reste : on le DIT,
      // une fois, plutot que de lever au milieu d'un clic.
      tracer?.('admin', 'ecran d administration absent du balisage');
      return;
    }
    const ici = document.querySelector('.view:not([hidden])')?.id;
    vueAvant = ici && ici !== 'v-admin' ? ici : 'v-reglages';
    // ⚠️ ON NE ROUVRE QUE CE QUI ETAIT OUVERT. Les reglages ont DEUX formes — la
    // pop-up par-dessus l'ecran courant, et la vue `v-reglages` a part entiere.
    // Rouvrir la pop-up au retour dans le second cas laisserait une page de
    // reglages VIDE derriere le voile.
    modaleAvant = !!($('voile') && !$('voile').hidden);
    // ⚠️ REFERMER LA POP-UP AVANT DE DEPLACER QUOI QUE CE SOIT. Quand elle est
    // ouverte, tout `#v-reglages .wrap` — le bloc Discord compris — vit dans
    // `#modale-corps`.
    if (modaleAvant) fermerModale?.();
    vue('v-admin');
    brancher();

    // ⚠️ LA PORTE D'ABORD, LES DONNEES ENSUITE — JAMAIS L'INVERSE.
    // Peindre puis masquer reviendrait a tout demander au serveur et a se
    // contenter de ne pas l'afficher : la liste des invites, les droits, les
    // prix, les sources auraient deja traverse le reseau.
    await lirePorte();
    peindreLaPorte();
    allerA(sectionCourante);
    if (porte.exigee && !porte.ouverte) return;

    await peupler();
  }

  /** Tout ce que le tableau de bord montre — appele une fois la porte passee. */
  async function peupler() {
    // `chargerMoi` d'abord : « tout valider » ne doit jamais partir sans savoir
    // s'il a le droit de cocher les sensibles.
    await chargerMoi();
    // Les deux sections se remplissent en parallele : elles ne dependent pas
    // l'une de l'autre, et la plus lente ne doit pas retenir l'autre.
    await Promise.all([chargerRoles(), chargerLesPersonnes(), chargerPoints(), chargerLaCharge()]);
    // ⚠️ UN DERNIER REPEINT, ET CE N'EST PAS DU CONFORT. Les deux chargements
    // posent chacun `roles` et `colonnes` : le dernier arrive fait foi. Si celui
    // des personnes n'est pas le dernier, ses pastilles de droits porteraient
    // les colonnes d'un chargement precedent — donc des sigles a cote de la
    // plaque, ou aucun.
    peindreLesPersonnes();
    peindreLaMatrice();
    chargerListeDiscord();
    chargerAcces?.();
    chargerLApercu();
    // Les blocs venus du panneau de reglages : add-ons, pre-mapping, photos,
    // journal, diagnostic, notifications, gouts, maintenance, seance, prets.
    preparerLAdministration?.();
    // Le registre commun par serveur : independant du reste, il ne retient rien.
    chargerLeRegistre();
  }

  // Deux gestes, et il n'y a plus de troisieme : plus rien a rendre a personne.
  function fermer() {
    vue($(vueAvant) ? vueAvant : 'v-reglages');
    if (modaleAvant) ouvrirModale?.();
  }

  // --- LE REGISTRE COMMUN : ce que chaque serveur Discord ouvre, PAR SERVEUR ---
  // Meme document que l'admin du lecteur 14e (registre partage) : on lit une
  // version, on ecrit avec If-Match. Un 409 veut dire que l'autre panneau a
  // ecrit entre-temps : on ne reessaie JAMAIS de force, on propose de recharger.
  const BOTS_REG = ['tsudoi', 'almighty'];
  const CATS_REG = [['films', 'Films'], ['series', 'Séries'], ['animes', 'Animés']];
  const reg = { doc: null, version: null, serveurs: [], roles: [], guilde: '', regles: [] };
  const listeDe = (v) => String(v ?? '').split(',').map((x) => x.trim()).filter(Boolean);
  const coche = (id) => $(id)?.checked === true;
  const valeurDe = (id) => String($(id)?.value ?? '').trim();
  const opt = (v, mot, choisi) => `<option value="${esc(v)}"${String(choisi ?? '') === String(v) ? ' selected' : ''}>${esc(mot)}</option>`;
  const caseReg = (id, mot, on) => `<label class="adm-reg-case"><input type="checkbox" id="${id}"${on ? ' checked' : ''} /> ${esc(mot)}</label>`;
  const champReg = (id, mot, controle) => `<div class="adm-pts-champ"><label for="${id}">${esc(mot)}</label>${controle}</div>`;
  const nomDuRole = (id) => {
    const r = reg.roles.find((x) => x.id === id);
    return r?.nom ?? r?.name ?? id;
  };

  function monterLeRegistre() {
    if ($('adm-zone-registre')) return $('adm-zone-registre');
    const page = document.querySelector('#adm-corps .adm-section[data-adm="acces"]');
    if (!page) return null;
    const zone = document.createElement('section');
    zone.className = 'adm-zone';
    zone.id = 'adm-zone-registre';
    zone.innerHTML = `<div class="adm-zone-tete">
        <p class="titre-section">Par serveur</p>
        <select id="adm-reg-guilde" aria-label="Serveur"></select>
        <button type="button" class="ghost" id="adm-reg-relire">Relire</button>
      </div>
      <p class="muted adm-aide">
        Ce que chaque serveur ouvre : ses bots, qui a la main, les points, l’accès et
        les réglages proposés aux nouveaux venus. Ce registre est partagé avec l’autre
        panneau d’administration : une modification faite là-bas n’est jamais écrasée ici.
      </p>
      <div class="adm-reg-conflit" id="adm-reg-conflit" role="alert" hidden>
        <span>Modifié dans l’autre panneau — recharger pour voir sa version, puis refaire ta modification.</span>
        <button type="button" id="adm-reg-recharger">Recharger</button>
      </div>
      <div id="adm-reg-form"></div>
      <div class="ajout-addon"><button type="button" id="adm-reg-enregistrer">Enregistrer ce serveur</button></div>
      <span class="etat-lang" id="adm-reg-etat"></span>
      <p class="titre-section adm-reg-sous">Réglages par personne</p>
      <p class="muted adm-aide">
        Ce qu’une personne garde pour elle, quel que soit le serveur. Un champ laissé sur
        « Défaut du serveur » suit les réglages du serveur ci-dessus.
      </p>
      <div class="ajout-addon">
        <input type="text" id="adm-reg-p-id" placeholder="Identifiant Discord de la personne" aria-label="Personne" list="adm-reg-p-liste" />
        <datalist id="adm-reg-p-liste"></datalist>
      </div>
      <div id="adm-reg-p-form"></div>
      <div class="ajout-addon"><button type="button" id="adm-reg-p-enregistrer">Enregistrer cette personne</button></div>
      <span class="etat-lang" id="adm-reg-p-etat"></span>
`;
    page.appendChild(zone);
    // L'aperçu et le journal sont du diagnostic : ils vivent dans « Journaux et
    // diagnostic », comme le reste de ce qui explique pourquoi quelqu'un ne voit rien.
    const diag = document.querySelector('#adm-corps .adm-section[data-adm="journaux"]');
    if (diag && !$('adm-zone-registre-diag')) {
      const z = document.createElement('section');
      z.className = 'adm-zone';
      z.id = 'adm-zone-registre-diag';
      z.innerHTML = `<div class="adm-zone-tete"><p class="titre-section">Ce que voit telle personne</p>
          <select id="adm-reg-ap-guilde" aria-label="Serveur de l’aperçu"></select></div>
        <div class="ajout-addon">
          <input type="text" id="adm-reg-ap-personne" placeholder="Identifiant Discord" aria-label="Personne" />
          <input type="text" id="adm-reg-ap-roles" placeholder="Rôles, séparés par des virgules" aria-label="Rôles" />
          <button type="button" class="ghost" id="adm-reg-ap-voir">Voir</button>
        </div>
        <div class="adm-reg-liste" id="adm-reg-apercu"></div>
        <p class="titre-section adm-reg-sous">Journal du registre</p>
        <div class="adm-reg-liste" id="adm-reg-journal"></div>`;
      diag.appendChild(z);
    }
    surClic('adm-reg-relire', () => chargerLeRegistre());
    surClic('adm-reg-recharger', () => chargerLeRegistre());
    surClic('adm-reg-enregistrer', () => enregistrerLeServeur());
    surClic('adm-reg-ap-voir', () => voirLApercu());
    surClic('adm-reg-p-enregistrer', () => enregistrerLaPersonne());
    const qui = $('adm-reg-p-id');
    if (qui) qui.onchange = () => peindreLaPersonne();
    const choix = $('adm-reg-guilde');
    if (choix) choix.onchange = () => { reg.guilde = choix.value; peindreLeServeur(); chargerLesRoles(); };
    return zone;
  }

  async function chargerLeRegistre() {
    if (!monterLeRegistre()) return;
    $('adm-reg-conflit').hidden = true;
    texte('adm-reg-etat', 'Lecture du registre…');
    try {
      // Les listes Discord sont un confort : leur panne n'empeche pas d'editer.
      const [r, s] = await Promise.all([
        api('/api/admin/registre'),
        Promise.resolve(lireServeursAdmin?.()).catch(() => null),
      ]);
      reg.doc = r?.doc ?? { serveurs: {}, personnes: {} };
      reg.version = r?.version ?? null;
      reg.serveurs = s?.serveurs ?? [];
      const ids = [...new Set([...Object.keys(reg.doc.serveurs ?? {}), ...reg.serveurs.map((x) => x.id)])];
      if (!ids.includes(reg.guilde)) reg.guilde = ids[0] ?? '';
      const choix = $('adm-reg-guilde');
      const options = ids.map((id) => opt(id, reg.doc.serveurs?.[id]?.nom
        || reg.serveurs.find((x) => x.id === id)?.nom || id, reg.guilde)).join('');
      for (const c of [choix, $('adm-reg-ap-guilde')]) if (c) { c.innerHTML = options; c.value = reg.guilde; }
      peindreLeServeur();
      chargerLesRoles();
      const dlp = $('adm-reg-p-liste');
      if (dlp) dlp.innerHTML = Object.keys(reg.doc.personnes ?? {}).map((id) => opt(id, id)).join('');
      peindreLaPersonne();
      texte('adm-reg-etat', ids.length ? '' : 'Aucun serveur : invite un des bots sur un serveur Discord, puis « Relire ».');
    } catch (err) {
      texte('adm-reg-etat', `Registre illisible : ${err.message} — « Relire » réessaie.`);
    }
    chargerLeJournal();
  }

  // Les rôles du serveur CHOISI (`?guild=`), pas ceux du serveur principal. Le
  // serveur renvoie `guild` : une réponse arrivée après un changement de serveur
  // est écartée, sinon on proposerait les rôles d'un autre serveur.
  async function chargerLesRoles() {
    const g = reg.guilde;
    reg.roles = [];
    if (!g) return;
    const ro = await api(`/api/admin/roles?guild=${encodeURIComponent(g)}`).catch(() => null);
    if (g !== reg.guilde || (ro?.guild && ro.guild !== g)) return;
    reg.roles = ro?.roles ?? [];
    const base = reg.doc?.serveurs?.[g]?.acces?.roleBase ?? '';
    const sel = $('adm-reg-role-base');
    if (sel) { const garde = sel.value || base; sel.innerHTML = optionsDesRoles(garde); sel.value = garde; }
    const dl = $('adm-reg-roles-liste');
    if (dl) dl.innerHTML = reg.roles.map((x) => opt(x.id, x.nom ?? x.name ?? x.id)).join('');
    peindreLesRegles();
  }

  // Un rôle de base absent de la liste reste affiché (et donc conservé) plutôt
  // que de retomber en silence sur « Aucun » au prochain enregistrement.
  function optionsDesRoles(choisi) {
    const connu = !choisi || reg.roles.some((x) => x.id === choisi);
    return [opt('', 'Aucun', choisi),
      ...(connu ? [] : [opt(choisi, `${choisi} (introuvable sur ce serveur)`, choisi)]),
      ...reg.roles.map((x) => opt(x.id, x.nom ?? x.name ?? x.id, choisi))].join('');
  }

  function peindreLeServeur() {
    const form = $('adm-reg-form');
    if (!form) return;
    if (!reg.guilde) { form.innerHTML = ''; return; }
    const s = reg.doc?.serveurs?.[reg.guilde] ?? {};
    const a = s.acces ?? {};
    const d = s.reglagesDefaut ?? {};
    const pts = s.points ?? {};
    reg.regles = [...(a.regles ?? [])];
    const roles = optionsDesRoles(a.roleBase ?? '');
    const commandes = BOTS_REG.map((b) => champReg(`adm-reg-cmd-${b}`, `Commandes actives — ${b}`,
      `<input type="text" id="adm-reg-cmd-${b}" value="${esc((s.commandes?.[b] ?? []).join(', '))}" placeholder="Toutes" />`)).join('');
    form.innerHTML = `<div class="adm-pts-grille">
        ${champReg('adm-reg-main', 'Qui a la main', `<select id="adm-reg-main">${BOTS_REG.map((b) => opt(b, b, s.main ?? 'tsudoi')).join('')}</select>`)}
        ${commandes}
        ${champReg('adm-reg-base', 'Base des points', `<select id="adm-reg-base">${opt('seance', 'Séance', pts.base ?? 'seance')}${opt('14e', '14e Division', pts.base ?? 'seance')}</select>`)}
        ${champReg('adm-reg-role-base', 'Rôle de base', `<select id="adm-reg-role-base">${roles}</select>`)}
        ${champReg('adm-reg-sans-role', 'Personnes sans ce rôle', `<select id="adm-reg-sans-role">${opt('aucun', 'Aucun accès', a.sansRole ?? 'aucun')}${opt('demande', 'Sur demande', a.sansRole)}${opt('direct', 'Accès direct', a.sansRole)}</select>`)}
        ${champReg('adm-reg-langue', 'Langue par défaut', `<select id="adm-reg-langue">${opt('fr', 'Français', d.langue ?? 'fr')}${opt('en', 'English', d.langue)}</select>`)}
        ${champReg('adm-reg-variante', 'Variante par défaut', `<input type="text" id="adm-reg-variante" value="${esc(d.variante ?? '')}" placeholder="Aucune" />`)}
      </div>
      <div class="adm-reg-cases"><span class="adm-pts-sous">Bots présents</span>${BOTS_REG.map((b) => caseReg(`adm-reg-bot-${b}`, b, (s.bots ?? []).includes(b))).join('')}</div>
      <div class="adm-reg-cases"><span class="adm-pts-sous">Catégories ouvertes</span>${CATS_REG.map(([c, mot]) => caseReg(`adm-reg-cat-${c}`, mot, a.categories?.[c] === true)).join('')}</div>
      <div class="adm-reg-cases">${caseReg('adm-reg-synchro', 'Points synchronisés entre les bots', pts.synchro === true)}${caseReg('adm-reg-flou', 'Flou par défaut', d.flou === true)}</div>
      <p class="titre-section adm-reg-sous">Règles par rôle, par personne, par œuvre</p>
      <div class="adm-reg-liste" id="adm-reg-regles"></div>
      <div class="ajout-addon">
        <select id="adm-reg-r-type" aria-label="Cible">${opt('role', 'Rôle')}${opt('personne', 'Personne')}</select>
        <input type="text" id="adm-reg-r-id" placeholder="Identifiant du rôle ou de la personne" aria-label="Identifiant" list="adm-reg-roles-liste" />
        <datalist id="adm-reg-roles-liste">${reg.roles.map((r) => opt(r.id, r.nom ?? r.name ?? r.id)).join('')}</datalist>
        <select id="adm-reg-r-cat" aria-label="Catégorie">${opt('', 'Toutes catégories')}${CATS_REG.map(([c, mot]) => opt(c, mot)).join('')}</select>
        <input type="text" id="adm-reg-r-oeuvre" placeholder="Œuvre (facultatif) — chercher" aria-label="Œuvre" list="adm-reg-oeuvres" />
        <datalist id="adm-reg-oeuvres"></datalist>
        <select id="adm-reg-r-effet" aria-label="Effet">${opt('autoriser', 'Autoriser')}${opt('bloquer', 'Bloquer')}</select>
        <button type="button" class="ghost" id="adm-reg-r-ajouter">Ajouter la règle</button>
      </div>`;
    surClic('adm-reg-r-ajouter', () => ajouterUneRegle());
    const oeuvre = $('adm-reg-r-oeuvre');
    if (oeuvre) oeuvre.oninput = () => chercherUneOeuvre(oeuvre.value);
    peindreLesRegles();
  }

  function peindreLesRegles() {
    const el = $('adm-reg-regles');
    if (!el) return;
    el.innerHTML = reg.regles.length ? reg.regles.map((r) => `<div class="adm-reg-ligne"><span>
        <strong>${r.effet === 'bloquer' ? 'Bloque' : 'Autorise'}</strong>
        ${r.cible?.type === 'personne' ? 'la personne' : 'le rôle'} ${esc(r.cible?.type === 'role' ? nomDuRole(r.cible.id) : r.cible?.id)}${r.categorie ? ` · ${esc(r.categorie)}` : ''}${r.oeuvre ? ` · œuvre ${esc(r.oeuvre)}` : ''}</span>
        <button type="button" class="ghost" data-reg-suppr>Retirer</button></div>`).join('')
      : '<p class="muted adm-aide">Aucune règle : le rôle de base et les catégories décident seuls.</p>';
    el.querySelectorAll('[data-reg-suppr]').forEach((b, i) => {
      b.onclick = () => { reg.regles.splice(i, 1); peindreLesRegles(); };
    });
  }

  function ajouterUneRegle() {
    const id = valeurDe('adm-reg-r-id');
    if (!id) { texte('adm-reg-etat', 'Une règle vise un rôle ou une personne : renseigne son identifiant.'); return; }
    const categorie = valeurDe('adm-reg-r-cat');
    const oeuvre = valeurDe('adm-reg-r-oeuvre');
    reg.regles.push({
      cible: { type: valeurDe('adm-reg-r-type') || 'role', id },
      ...(categorie ? { categorie } : {}),
      ...(oeuvre ? { oeuvre } : {}),
      effet: valeurDe('adm-reg-r-effet') || 'autoriser',
    });
    texte('adm-reg-etat', 'Règle ajoutée — « Enregistrer ce serveur » pour l’appliquer.');
    peindreLesRegles();
  }

  // La recherche catalogue existante (`/api/recherche`), celle de la barre du haut.
  let rechercheReg = 0;
  async function chercherUneOeuvre(q) {
    const mien = ++rechercheReg;
    const liste = $('adm-reg-oeuvres');
    if (!liste || String(q).trim().length < 2) return;
    try {
      const r = await api(`/api/recherche?q=${encodeURIComponent(q)}`);
      if (mien !== rechercheReg) return;
      liste.innerHTML = (r?.resultats ?? []).slice(0, 20)
        .map((m) => opt(m.id, m.name ?? m.title ?? m.id)).join('');
    } catch (err) {
      texte('adm-reg-etat', `Recherche d’œuvre impossible : ${err.message}`);
    }
  }

  async function enregistrerLeServeur() {
    if (!reg.guilde) return;
    const avant = reg.doc?.serveurs?.[reg.guilde] ?? {};
    const valeur = {
      ...avant,
      nom: avant.nom ?? reg.serveurs.find((x) => x.id === reg.guilde)?.nom ?? '',
      bots: BOTS_REG.filter((b) => coche(`adm-reg-bot-${b}`)),
      main: valeurDe('adm-reg-main') || 'tsudoi',
      commandes: Object.fromEntries(BOTS_REG.map((b) => [b, listeDe(valeurDe(`adm-reg-cmd-${b}`))]).filter(([, l]) => l.length)),
      points: { ...avant.points, base: valeurDe('adm-reg-base') || 'seance', synchro: coche('adm-reg-synchro') },
      acces: {
        ...avant.acces,
        roleBase: valeurDe('adm-reg-role-base') || null,
        sansRole: valeurDe('adm-reg-sans-role') || 'aucun',
        categories: Object.fromEntries(CATS_REG.map(([c]) => [c, coche(`adm-reg-cat-${c}`)])),
        regles: reg.regles,
      },
      reglagesDefaut: {
        ...avant.reglagesDefaut,
        langue: valeurDe('adm-reg-langue') || 'fr',
        flou: coche('adm-reg-flou'),
        variante: valeurDe('adm-reg-variante') || null,
      },
    };
    texte('adm-reg-etat', 'Enregistrement…');
    try {
      const r = await api(`/api/admin/registre/serveurs/${encodeURIComponent(reg.guilde)}`, {
        method: 'PUT',
        headers: { 'if-match': String(reg.version) },
        body: JSON.stringify({ valeur }),
      });
      reg.version = r?.version ?? reg.version;
      reg.doc.serveurs = { ...reg.doc.serveurs, [reg.guilde]: valeur };
      texte('adm-reg-etat', 'Enregistré.');
      chargerLeJournal();
    } catch (err) {
      if (err.status === 409) {
        // Rien n'est reecrit : la version de l'autre panneau reste la seule.
        $('adm-reg-conflit').hidden = false;
        texte('adm-reg-etat', 'Non enregistré : le registre a changé depuis ta lecture.');
        return;
      }
      // 400 : le serveur dit quel champ il refuse (`erreurs`), on le répète tel quel.
      const quoi = Array.isArray(err.erreurs) && err.erreurs.length ? err.erreurs.join(' ; ') : err.message;
      texte('adm-reg-etat', `Non enregistré : ${quoi}`);
    }
  }

  // --- PAR PERSONNE : `doc.personnes[id]` = { langue?, flou?, variante? } ---
  // Un champ absent suit le serveur (reglagesDe) : « Défaut du serveur » RETIRE
  // la clé au lieu d'écrire une valeur, sinon la personne serait figée à jamais.
  function peindreLaPersonne() {
    const form = $('adm-reg-p-form');
    if (!form) return;
    const id = valeurDe('adm-reg-p-id');
    if (!id) { form.innerHTML = ''; texte('adm-reg-p-etat', 'Renseigne une personne pour voir et régler ses réglages.'); return; }
    const p = reg.doc?.personnes?.[id] ?? {};
    const flou = p.flou === undefined ? '' : String(p.flou);
    form.innerHTML = `<div class="adm-pts-grille">
        ${champReg('adm-reg-p-langue', 'Langue', `<select id="adm-reg-p-langue">${opt('', 'Défaut du serveur', p.langue ?? '')}${opt('fr', 'Français', p.langue)}${opt('en', 'English', p.langue)}</select>`)}
        ${champReg('adm-reg-p-flou', 'Flou', `<select id="adm-reg-p-flou">${opt('', 'Défaut du serveur', flou)}${opt('true', 'Oui', flou)}${opt('false', 'Non', flou)}</select>`)}
        ${champReg('adm-reg-p-variante', 'Variante', `<input type="text" id="adm-reg-p-variante" value="${esc(p.variante ?? '')}" placeholder="Défaut du serveur" />`)}
      </div>`;
    texte('adm-reg-p-etat', reg.doc?.personnes?.[id] ? '' : 'Aucun réglage propre : tout suit le serveur.');
  }

  async function enregistrerLaPersonne() {
    const id = valeurDe('adm-reg-p-id');
    if (!id) { texte('adm-reg-p-etat', 'Renseigne d’abord l’identifiant de la personne.'); return; }
    const valeur = { ...reg.doc?.personnes?.[id] };
    for (const k of ['langue', 'flou', 'variante']) delete valeur[k];
    const langue = valeurDe('adm-reg-p-langue');
    const flou = valeurDe('adm-reg-p-flou');
    const variante = valeurDe('adm-reg-p-variante');
    if (langue) valeur.langue = langue;
    if (flou) valeur.flou = flou === 'true';
    if (variante) valeur.variante = variante;
    texte('adm-reg-p-etat', 'Enregistrement…');
    try {
      const r = await api(`/api/admin/registre/personnes/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'if-match': String(reg.version) },
        body: JSON.stringify({ valeur }),
      });
      reg.version = r?.version ?? reg.version;
      reg.doc.personnes = { ...reg.doc.personnes, [id]: valeur };
      texte('adm-reg-p-etat', 'Enregistré.');
      chargerLeJournal();
    } catch (err) {
      if (err.status === 409) {
        $('adm-reg-conflit').hidden = false;
        texte('adm-reg-p-etat', 'Non enregistré : le registre a changé depuis ta lecture.');
        return;
      }
      const quoi = Array.isArray(err.erreurs) && err.erreurs.length ? err.erreurs.join(' ; ') : err.message;
      texte('adm-reg-p-etat', `Non enregistré : ${quoi}`);
    }
  }

  async function voirLApercu() {
    const el = $('adm-reg-apercu');
    if (!el || !reg.guilde) return;
    const q = new URLSearchParams({
      guilde: valeurDe('adm-reg-ap-guilde') || reg.guilde,
      personne: valeurDe('adm-reg-ap-personne'),
      roles: listeDe(valeurDe('adm-reg-ap-roles')).join(','),
    });
    el.innerHTML = '<p class="muted adm-aide">Calcul…</p>';
    try {
      const liste = await api(`/api/admin/registre/apercu?${q}`);
      const tout = Array.isArray(liste) ? liste : [];
      const vues = tout.filter((x) => x.voit);
      el.innerHTML = vues.length
        ? `<p class="adm-pts-sous">${vues.length} œuvre(s) visible(s) sur ${tout.length}</p>${vues.slice(0, 60).map((x) => `<div class="adm-reg-ligne"><span>${esc(x.titre ?? x.id)}</span><span class="muted">${esc(x.raison ?? '')}</span></div>`).join('')}`
        : `<p class="muted adm-aide">Cette personne ne voit aucune œuvre sur ce serveur${tout.length ? ` (${esc(tout[0].raison ?? '')})` : ''}.</p>`;
    } catch (err) {
      el.innerHTML = `<p class="muted adm-aide">Aperçu impossible : ${esc(err.message)}</p>`;
    }
  }

  async function chargerLeJournal() {
    const el = $('adm-reg-journal');
    if (!el) return;
    try {
      const j = await api('/api/admin/registre/journal?limite=50');
      const lignes = Array.isArray(j) ? j : [];
      const court = (v) => esc((v === undefined ? '—' : JSON.stringify(v)).slice(0, 160));
      const quand = (q) => (q ? new Date(q).toLocaleString('fr-FR') : 'date inconnue');
      el.innerHTML = lignes.length ? lignes.map((e) => `<details class="adm-reg-ligne"><summary>
          <span>${esc(quand(e.quand))} · ${esc(e.qui ?? 'inconnu')} · ${esc(e.chemin ?? '')}</span>
          <span class="muted">${esc(e.panneau ?? '')}</span></summary>
          <p class="adm-pts-sous">Avant : ${court(e.avant)}</p><p class="adm-pts-sous">Après : ${court(e.apres)}</p></details>`).join('')
        : '<p class="muted adm-aide">Aucune modification enregistrée pour l’instant.</p>';
    } catch (err) {
      el.innerHTML = `<p class="muted adm-aide">Journal illisible : ${esc(err.message)}</p>`;
    }
  }

  // ⚠️ `rendreLeBloc` A DISPARU DE CE CONTRAT, ET C'EST VOULU.
  // `main.js` l'appelait au debut de `chargerAcces` pour rapatrier un bloc reste
  // dans une section masquee. Il n'y a plus de bloc voyageur : garder la
  // methode en la faisant ne rien faire aurait laisse croire qu'un rapatriement
  // avait encore lieu quelque part.
  return { ouvrir, fermer };
}
